/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2020, 2022
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import IdGenerator from './IdGenerator';
import ModuleVersion from './ModuleVersion';
import UseSpecType from './UseSpecType';
import ModuleRelinker from './ModuleRelinker';
import EmbeddedModuleRelinker from './EmbeddedModuleRelinker';
import DataModuleExtractor from './DataModuleExtractor';
import QuerySubjectUtils from './QuerySubjectUtils';
import InstanceType from './InstanceType';
import MoserObjectUtils from './MoserObjectUtils';
import ReferenceResolver from './ReferenceResolver';
import PropertySetter from './PropertySetter';
import CopyMode from './CopyMode';
import CommonResolverUtil from './CommonResolverUtil';
import RelationshipUtils from './RelationshipUtils';
import MoserCommonUtils from './MoserCommonUtils';
import QueryItem from './QueryItem';
import MemberType from './MemberType';
import MoserObject from './MoserObject';
import PropertyType from './PropertyType';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/client/utils/IdGenerator (static) // generateNewUseSpecId, new, generateNewUseSpecIdWithPrefix, makeIdentifier
//@import com/ibm/bi/platform/moser/common/generated/metadata/ModuleVersion (static) // ModuleVersion
//@import com/ibm/bi/platform/moser/common/generated/metadata/UseSpecType (static) // UseSpecType
//@import com/ibm/bi/platform/moser/client/relink/ModuleRelinker (runtime) // new
//@import com/ibm/bi/platform/moser/client/relink/EmbeddedModuleRelinker (runtime) // new
//@import com/ibm/bi/platform/moser/core/module/util/DataModuleExtractor (static) // removeMembersFromQS, removeModuleMetadata, getUseSpecRefs, getUseSpec, getUseSpecByRef
//@import com/ibm/bi/platform/moser/client/utils/QuerySubjectUtils (static) // getParentModule, clearPropertyOerride, convertReferenceQuerySubjectToCopy, getQueryItemById, getUseSpecById
//@import com/ibm/bi/platform/moser/common/generated/metadata/InstanceType (static) // InstanceType
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (static) // getList, getQueryItems
//@import com/ibm/bi/platform/moser/common/utils/ReferenceResolver (static) // strBetween, resolve, getFirstPart, getMoserObjectByIdForExpression, bindMoserObject, resolveRelationship, strAfter, getSecondPart
//@import com/ibm/bi/platform/moser/common/generated/metadata/PropertySetter (static) // getAllQueryItemsInQuerySubject, cleanQuerySubjectItemNormalization, addQueryItemToItemNormalization, setIdentifier, getExtractIdentifiersSpec, getParentQuerySubject, updateIdForExpression, resetReferencedObject, isEmptyRef
//@import com/ibm/bi/platform/moser/client/utils/CopyMode (static) // CopyMode
//@import com/ibm/bi/platform/moser/core/metadata/util/CommonResolverUtil (static) // getFMPlaceHolderInMDTV
//@import com/ibm/bi/platform/moser/client/utils/RelationshipUtils (static) // isEqual
//@import com/ibm/bi/platform/moser/common/utils/MoserCommonUtils (static) // areCompatibleDataTypes, getScale, generateHighlevelDatatype, createAncestors, getPrecision
//@import com/ibm/bi/platform/moser/common/generated/metadata/QueryItem (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/MemberType (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/MoserObject (static) // getModuleObject
//@import com/ibm/bi/platform/moser/common/generated/metadata/PropertyType (static) // new

/**
 * This class provides a set of static utility APIs for manipulate a data module. This class will be translated to JavaScript If a function is used only for traverse, don't use getXXX (it might create an empty list). use basicGetXXX.
 * @author  mohamedelgemaiey
 */
export default class ModuleUtils extends BaseMoserObject{

	/**
	 * Create a new module
	 * @param {ObjectFactory} objectFactory  : the object factory that will be used to create the module
	 * @param {String} label  : the module label
	 * @param {String} expressionLocale  : the expression locale
	 * @return {Module}
	 */
	static createModule(objectFactory, label, expressionLocale) {
		var module = objectFactory.createModule();
		module.setLabel(label);
		module.setContainer("C");
		new IdGenerator(null).generateId(module);
		module.setVersion(ModuleVersion.LATEST_VERSION);
		module.setExpressionLocale(expressionLocale);
		module.setDataRetrievalMode("liveConnection");
		var f = objectFactory.createFolderType();
		module.addMetadataTreeView(f);
		return module;
	}

	/**
	 * Add a source to a module
	 * @param {Module} module  : the  module
	 * @param {UseSpecType} srcModuleType  : Source type (package, module, file, ...etc)
	 * @param {String} srcModuleStoreId  : source module store id
	 * @param {String} srcModuleSearchPath  : source module search path (Deprecated, ignored)
	 * @param  objectFactory: factory to create new module objects
	 * @param {boolean} addMDTVPlaceHolder  : true to add a place holder in the MDTV (Only for FM package and OLAP)
	 * @param  ancestors: the ancestors of the useSpec
	 * @return {UseSpec}  newly created UseSpec for this source
	 */
	static addSource(module, srcModuleType, srcModuleStoreId, srcModuleSearchPath, objectFactory, addMDTVPlaceHolder, ancestors) {
		var useSpec = objectFactory.createModuleUseSpec();
		useSpec.setIdentifier(IdGenerator.generateNewUseSpecId(module));
		useSpec.setType(srcModuleType);
		useSpec.setStoreID(srcModuleStoreId);
		if (ancestors && !(ancestors.length==0)) {
			Array.prototype.push.apply(useSpec.getAncestors(), ancestors);
		}
		module.addUseSpec(useSpec);
		if (addMDTVPlaceHolder && (UseSpecType.PACKAGE === srcModuleType || UseSpecType.LW_OLAP === srcModuleType)) {
			ModuleUtils._addPlaceHolder(module, useSpec.getIdentifier(), objectFactory);
		}
		if (UseSpecType.DATABASE === srcModuleType) {
			useSpec.setDataCacheExpiry("3600");
		}
		return useSpec;
	}

	/**
	 * Relink a source in a Data Module
	 * @param {Module} module  : the module
	 * @param {UseSpec} useSpec  : the useSpec to relink
	 * @param {UseSpecType} newSrcType  : the new source type (package, module, file, ...etc)
	 * @param {String} newSrcStoreId  : the new source module store id
	 * @param  newSrcAncestors: the ancestors of the useSpec
	 * @param  objectFactory: factory to create new module objects
	 * @return {UseSpec}  newly created UseSpec for the relinked source
	 */
	static relinkDataModuleSource(module, useSpec, newSrcType, newSrcStoreId, newSrcAncestors, objectFactory) {
		var relinker = new ModuleRelinker(module, null);
		return relinker.relinkSource(useSpec, newSrcType, newSrcStoreId, newSrcAncestors, objectFactory);
	}

	/**
	 * Relink a data module which contains references to a particular useSpec so they use identifiers from newSrcModule.
	 * @param {Module} module  : the module
	 * @param {UseSpec} useSpec  : the useSpec to relink identifiers for
	 * @param {Module} newSrcModule  : the new module to relink identifiers to
	 */
	static relinkDataModule(module, useSpec, newSrcModule) {
		var relinker = new ModuleRelinker(module, newSrcModule);
		relinker.relinkIdentifiers(useSpec);
	}

	/**
	 * Relink a source in an Embedded Module
	 * @param {Module} module  : the embedded module
	 * @param {UseSpec} useSpec  : the useSpec to relink
	 * @param {UseSpecType} newSrcType  : the new source type (package, module, file, ...etc)
	 * @param {String} newSrcStoreId  : the new source module store id
	 * @param  newSrcAncestors: the ancestors of the useSpec
	 * @param  objectFactory: factory to create new module objects
	 * @return {UseSpec}  newly created UseSpec for the relinked source
	 */
	static relinkEmbeddedModuleSource(module, useSpec, newSrcType, newSrcStoreId, newSrcAncestors, objectFactory) {
		var relinker = new EmbeddedModuleRelinker(module, null);
		return relinker.relinkSource(useSpec, newSrcType, newSrcStoreId, newSrcAncestors, objectFactory);
	}

	/**
	 * Relink a embedded module which contains references to a particular useSpec so they use identifiers from newSrcModule.
	 * @param {Module} module  : the module
	 * @param {UseSpec} useSpec  : the useSpec to relink identifiers for
	 * @param {Module} newSrcModule  : the new module to relink identifiers to
	 */
	static relinkEmbeddedModule(module, useSpec, newSrcModule) {
		var relinker = new EmbeddedModuleRelinker(module, newSrcModule);
		relinker.relinkIdentifiers(useSpec);
	}

	/**
	 * Relink idForExpression's, used against an embedded module, from one source to another.
	 * @param {Module} module  : Can be either: 1- Currently resolved and working Module (some sources exists) 2- The embedded module in raw form (dashboard has shaping and source no longer exists) 3- Null (no sources or dashboard shaping exists)
	 * @param {Array} expressions  : list of idForExpressions to relink
	 * @param {Module} newSrcModule  : the new module to relink identifiers to
	 * @return {Array}  A list of relinked idForExpressions, in the same order as provided.
	 */
	static relinkIdForExpressions(module, expressions, newSrcModule) {
		var relinker = new EmbeddedModuleRelinker(module, newSrcModule);
		return relinker.relinkIdForExpressions(expressions);
	}

	/**
	 * Removes the members in a QuerySubject.
	 * @param {QuerySubject} qs  - an QuerySubject to have members removed from
	 */
	static removeMembers(qs) {
		DataModuleExtractor.removeMembersFromQS(qs);
	}

	/**
	 * Add a place holder for fm package tree view
	 * @param {Module} module
	 * @param {String} srcId
	 * @param {ObjectFactory} objectFactory
	 */
	static _addPlaceHolder(module, srcId, objectFactory) {
		var placeHolder = objectFactory.createFolderType();
		placeHolder.setIdentifier(srcId);
		var fi = objectFactory.createFolderItemType();
		fi.setFolder(placeHolder);
		var f;
		var mdtv = module.getMetadataTreeView();
		if (mdtv.length==0) {
			f = objectFactory.createFolderType();
			module.addMetadataTreeView(f);
		} else {
			f = mdtv[0];
		}
		f.addFolderItem(fi);
	}

	/**
	 * Prepare the module metadata for saving either a newly created data module or the changes made to an existing data module prior to the REST call: POST "/modules" or PUT "/modules/{moduleId}"
	 * @param {Module} module  - an Module object with metadata to be saved
	 * @return {Object}  a JSON object
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static prepareModuleForSave(module) {
		var moduleJSON;
		var clonedModule = module.clone();
		if (clonedModule) {
			DataModuleExtractor.removeModuleMetadata(clonedModule);
			moduleJSON = clonedModule.toJSON();
		}
		return moduleJSON;
	}

	/**
	 * Generate and set the object id in a given scope.
	 * @param {ObjectType} scope  : The object parent.
	 * @param {ObjectType} obj  : The object that will needs an id.
	 */
	static generateObjectId(scope, obj) {
		new IdGenerator(scope).generateId(obj);
	}

	/**
	 * Create and initialized an id generator. The caller can then use the returned id generator to create an id for multiple objects by calling generateId(obj).
	 * @return {IdGenerator}  an Id generator that is initialized with the given scope.
	 */
	static getIdGenerator(scope) {
		return new IdGenerator(scope);
	}

	/**
	 * Generate a new use spec id.
	 * @param {Module} module  : The module object.
	 * @return {String}  the new use spec id.
	 */
	static getNewUseSpecId(module) {
		return IdGenerator.generateNewUseSpecId(module);
	}

	/**
	 * Break the query subject reference and convert it to a copy
	 * @param   qs: the query subject
	 * @param   module: The module that includes the query subject
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static convertReferenceQuerySubjectToCopy(qs, module) {
		QuerySubjectUtils.convertReferenceQuerySubjectToCopy(qs, module);
	}

	/**
	 * insert querysubject into module, and, add it to MDTV.
	 * @param {Module} module  		: destination module.
	 * @param {Array} qsList 		: querysubject list.
	 * @param {FolderType} mdtvFolder 	: MDTV.
	 * @param {int} inserAtIndex 	: position.
	 * @param {ObjectFactory} objectFactory  : factory.
	 */
	static insertQuerySubject(module, qsList, mdtvFolder, inserAtIndex, objectFactory) {
		var mdtvIndex = inserAtIndex;
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			module.addQuerySubject(qs);
			ModuleUtils._insertFolderItem(mdtvFolder, qs, mdtvIndex++, objectFactory);
		}
	}

	/**
	 * get SourceModule UseSpec In ParentModule.
	 * @param {MoserObject} srcObj 	source object.
	 * @param {Module} module 	parent module.
	 * @return {UseSpec}  UseSpec or null.
	 */
	static getSourceModuleUseSpecInParentModule(srcObj, module) {
		if (!srcObj || !module) {
			return null;
		}
		var useSpecs = module.basicGetUseSpec();
		if (!useSpecs || useSpecs.length==0) {
			return null;
		}
		var srcModule = QuerySubjectUtils.getParentModule(srcObj);
		if (!srcModule) {
			return null;
		}
		var useLst = srcModule.basicGetUse();
		if (!useLst || useLst.length != 1) {
			return null;
		}
		var srcModuleStoreId = useLst[0];
		for (var __i_enFor0 = 0, __exp_enFor0 = useSpecs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var useSp = __exp_enFor0[__i_enFor0];
			if (srcModuleStoreId == useSp.getStoreID()) {
				return useSp;
			}
		}
		return null;
	}

	/**
	 * get the source module alias from usespec in module.
	 * @param {String} srcModuleId 	existing srcModuleId.
	 * @param {Module} module 		module.
	 * @param {MoserObject} srcObj 		source object.
	 * @return {String}  source module alias.
	 */
	static getSourceModuleAliasIfNotDefined(srcModuleId, module, srcObj) {
		if (srcModuleId != null) {
			return srcModuleId;
		}
		var useSpec = ModuleUtils.getSourceModuleUseSpecInParentModule(srcObj, module);
		if (useSpec) {
			return useSpec.getIdentifier();
		}
		return null;
	}

	/**
	 * Insert query subjects, relationship, calculations, filters into a module. This is typically used to insert a proposal into a module. This method will update the mdtv to insert the ref to the new objects. In case of copying query subject, if it already exists, it restore the deleted query items
	 * @param {ObjectType} destParent  : destination module (note that in the future this should be other objects too)
	 * @param {Array} objList  : List of moser objects (currently: query subjects, relationships, calculations, filters, query items)
	 * @param {String} srcModuleId  : the source module id. i.e. module.usespec[src module].identifier
	 * @param {Module} srcModule  : the source module
	 * @param {boolean} asReference  : as reference or copy
	 * @param {FolderType} mdtvFolder  : where to insert the objects in the mdtv
	 * @param {int} insertAtIndex  : where to insert the objects in the mdtvFolder
	 * @param {ObjectFactory} objectFactory  : to create the new objects (if clone is not used)
	 * @param {Object} idMap  : identifier map, storing old to new identifiers
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static insert(destParent, objList, srcModuleId, srcModule, asReference, mdtvFolder, insertAtIndex, objectFactory, idMap) {
		var idGenerator = ModuleUtils.getIdGenerator(destParent);
		var oldIdToNewIdMap = idMap;
		if (!oldIdToNewIdMap) {
			oldIdToNewIdMap = {};
		}
		var mdtvIndex = insertAtIndex;
		var destParentObjectType = destParent.getObjectType();
		for (var __i_enFor0 = 0, __exp_enFor0 = objList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			var objectType = obj.getObjectType();
			if ("QuerySubject" == objectType && "Module" == destParentObjectType) {
				var module = destParent;
				var qsRef = ModuleUtils.getSourceModuleAliasIfNotDefined(srcModuleId, module, obj) + "." + (obj).getIdentifier();
				var existingQS = ModuleUtils._findQuerySubjectByOriginalRefThenRef(module, qsRef);
				if (!existingQS) {
					var qs = obj.clone();
					if (qs.basicGetSecurityFilter()) {
						qs.getSecurityFilter().length = 0;
					}
					qs.setInstanceType(asReference ? InstanceType.REFERENCE : null);
					QuerySubjectUtils.clearPropertyOerride(qs);
					var oldId = qs.getIdentifier();
					ModuleUtils._updateQuerySubjectRef(module, qs, ModuleUtils.getSourceModuleAliasIfNotDefined(srcModuleId, module, obj), srcModule, asReference);
					idGenerator.generateId(qs);
					oldIdToNewIdMap[oldId] = qs.getIdentifier();
					var idForExprPrefix = qs.getIdentifier() + ".";
					for (var __i_enFor1 = 0, __exp_enFor1 = MoserObjectUtils.getQueryItems(qs), __len_enFor1 = __exp_enFor1.length;
							__i_enFor1 < __len_enFor1; ++__i_enFor1) {
						var qi = __exp_enFor1[__i_enFor1];
						if (!asReference) {
							qi.setExpression(qi.getIdentifier());
						}
						qi.setIdForExpression(idForExprPrefix + qi.getIdentifier());
					}
					module.addQuerySubject(qs);
					ModuleUtils._insertFolderItem(mdtvFolder, qs, mdtvIndex++, objectFactory);
				} else if (!asReference) {
					var qs = obj;
					ModuleUtils._updateExistingQuerySubject(existingQS, qs, objectFactory);
				}
			} else if ("Relationship" == objectType && "Module" == destParentObjectType) {
				var module = destParent;
				var rel = obj.clone();
				var qsId = oldIdToNewIdMap[rel.getLeft().getRef()];
				if (qsId != null) {
					rel.getLeft().setRef(qsId);
				}
				qsId = oldIdToNewIdMap[rel.getRight().getRef()];
				if (qsId != null) {
					rel.getRight().setRef(qsId);
				}
				if (!ModuleUtils._findRelationshipMatch(module, rel)) {
					var qsLeft = ModuleUtils.getQuerySubjectById(rel.getLeft().getRef(), module);
					var qsRight = ModuleUtils.getQuerySubjectById(rel.getRight().getRef(), module);
					if (qsLeft && qsLeft.getInstanceType() == InstanceType.REFERENCE && qsRight && qsRight.getInstanceType() == InstanceType.REFERENCE) {
						rel.setInstanceType(InstanceType.REFERENCE);
						rel.setRef(ModuleUtils.getSourceModuleAliasIfNotDefined(srcModuleId, module, obj) + "." + rel.getIdentifier());
					} else {
						rel.setInstanceType(null);
						rel.setRef(null);
					}
					idGenerator.generateId(rel);
					module.addRelationship(rel);
				}
				ReferenceResolver.resolveRelationship(rel, module);
			} else if ("Calculation" == objectType && "Module" == destParentObjectType) {
				var module = destParent;
				var calc = obj.clone();
				idGenerator.generateId(calc);
				module.addCalculation(calc);
				ModuleUtils._insertFolderItem(mdtvFolder, calc, mdtvIndex++, objectFactory);
			} else if ("Filter" == objectType && "Module" == destParentObjectType) {
				var module = destParent;
				var filter = obj.clone();
				idGenerator.generateId(filter);
				module.addFilter(filter);
				ModuleUtils._insertFolderItem(mdtvFolder, filter, mdtvIndex++, objectFactory);
			} else if ("FolderItemType" == objectType && "Module" == destParentObjectType) {
				var module = destParent;
				var folderItem = obj.clone();
				var folder = folderItem.getFolder();
				var o = folderItem.getReferencedObject();
				if (folder) {
					var folders = [];
					folders.push(folder);
					ModuleUtils.insert(module, folders, ModuleUtils.getSourceModuleAliasIfNotDefined(srcModuleId, module, obj), srcModule, asReference, mdtvFolder, mdtvIndex++, objectFactory, oldIdToNewIdMap);
				} else if (o) {
					var objects = [];
					objects.push(o);
					ModuleUtils.insert(module, objects, ModuleUtils.getSourceModuleAliasIfNotDefined(srcModuleId, module, obj), srcModule, asReference, mdtvFolder, mdtvIndex++, objectFactory, oldIdToNewIdMap);
				}
			} else if ("FolderType" == objectType && "Module" == destParentObjectType) {
				var module = destParent;
				var folder = obj.clone();
				idGenerator.generateId(folder);
				var items = folder.basicGetFolderItem();
				if (items && !(items.length==0)) {
					var folderItems = [];
					Array.prototype.push.apply(folderItems, items);
					folder.basicGetFolderItem().length = 0;
					ModuleUtils.insert(module, folderItems, ModuleUtils.getSourceModuleAliasIfNotDefined(srcModuleId, module, obj), srcModule, asReference, folder, 0, objectFactory, oldIdToNewIdMap);
				}
				ModuleUtils._insertFolder(mdtvFolder, folder, mdtvIndex++, objectFactory);
			} else if ("QueryItem" == objectType && "Module" == destParentObjectType) {
				var module = destParent;
				var ancestor = ["QuerySubject"];
				var srcQs = ModuleUtils.getTreeAncestor(obj, ancestor, null);
				var qsRef = ModuleUtils.getSourceModuleAliasIfNotDefined(srcModuleId, module, obj) + "." + srcQs.getIdentifier();
				var existingQS = ModuleUtils._findQuerySubjectByOriginalRefThenRef(module, qsRef);
				if (!existingQS) {
					existingQS = srcQs.clone();
					if (existingQS.basicGetSecurityFilter()) {
						existingQS.getSecurityFilter().length = 0;
					}
					existingQS.setInstanceType(asReference ? InstanceType.REFERENCE : null);
					QuerySubjectUtils.clearPropertyOerride(existingQS);
					var oldId = existingQS.getIdentifier();
					ModuleUtils._updateQuerySubjectRef(module, existingQS, ModuleUtils.getSourceModuleAliasIfNotDefined(srcModuleId, module, obj), srcModule, asReference);
					idGenerator.generateId(existingQS);
					oldIdToNewIdMap[oldId] = existingQS.getIdentifier();
					module.addQuerySubject(existingQS);
					ModuleUtils._insertFolderItem(mdtvFolder, existingQS, mdtvIndex++, objectFactory);
					for (var __i_enFor2 = 0, __exp_enFor2 = MoserObjectUtils.getQueryItems(existingQS), __len_enFor2 = __exp_enFor2.length;
							__i_enFor2 < __len_enFor2; ++__i_enFor2) {
						var qi = __exp_enFor2[__i_enFor2];
						ModuleUtils._removeObjectFromQuerySubject(qi, true);
					}
				}
				var idForExprPrefix = existingQS.getIdentifier() + ".";
				var qiMap = ModuleUtils._createQIMap(existingQS);
				var qi = obj;
				if (!qiMap[qi.getIdentifier()]) {
					var newQI = obj.clone();
					newQI.setExpression(newQI.getIdentifier());
					newQI.setIdForExpression(idForExprPrefix + newQI.getIdentifier());
					var newItem = objectFactory.createItemType();
					newItem.setQueryItem(newQI);
					existingQS.addItemAt(insertAtIndex, newItem);
				}
			}
		}
	}

	/**
	 * Copy query subjects, relationship, calculations, filters, folders from/into a module. This is typically used during a copy/paste. This method will update the mdtv to insert the ref to the new objects.
	 * @param {Module} module  : destination module
	 * @param {Array} objList  : List of moser objects (currently: query subjects, relationships, calculations, filters, folders)
	 * @param {FolderType} mdtvFolder  : the mdtvFolder to insert the object into
	 * @param {int} insertAtIndex  : where to insert the objects in the mdtvFolder
	 * @param {CopyMode} mode  : whether to copy common relationships, all relationships or none at all
	 * @param {ObjectFactory} objectFactory  : to create the new objects (if clone is not used)
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static copy(module, objList, mdtvFolder, insertAtIndex, mode, objectFactory) {
		var idGenerator = ModuleUtils.getIdGenerator(module);
		var oldIdToNewIdMap = {};
		var mdtvIndex = insertAtIndex;
		var copiedObj = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = objList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			copiedObj.push(ModuleUtils._copyMoserObject(module, obj, mdtvFolder, mdtvIndex++, idGenerator, oldIdToNewIdMap, objectFactory));
		}
		var relationships = ModuleUtils._collectRelationshipsToCopy(module, oldIdToNewIdMap, mode);
		for (var __i_enFor1 = 0, __exp_enFor1 = relationships, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var relationship = __exp_enFor1[__i_enFor1];
			ModuleUtils._copyRelationship(module, relationship, idGenerator, oldIdToNewIdMap);
		}
		return copiedObj;
	}

	/**
	 * Copy a MoserObject and insert it into the mdtvFolder.
	 * @param {MoserObject} obj
	 * @param {FolderType} mdtvFolder
	 * @param {int} mdtvIndex
	 * @param {IdGenerator} idGenerator
	 * @param {Object} oldIdToNewIdMap
	 * @param {ObjectFactory} objectFactory
	 * @return {MoserObject}  The copied MoserObject
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static _copyMoserObject(module, obj, mdtvFolder, mdtvIndex, idGenerator, oldIdToNewIdMap, objectFactory) {
		var objectType = obj.getObjectType();
		if ("QuerySubject" == objectType) {
			return ModuleUtils._copyQuerySubject(module, obj, mdtvFolder, mdtvIndex, idGenerator, oldIdToNewIdMap, objectFactory);
		} else if ("Relationship" == objectType) {
			return ModuleUtils._copyRelationship(module, obj, idGenerator, oldIdToNewIdMap);
		} else if ("Calculation" == objectType) {
			return ModuleUtils._copyCalculation(module, obj, mdtvFolder, mdtvIndex, idGenerator, objectFactory);
		} else if ("Filter" == objectType) {
			return ModuleUtils._copyFilter(module, obj, mdtvFolder, mdtvIndex, idGenerator, objectFactory);
		} else if ("FolderItemType" == objectType) {
			ModuleUtils._copyFolderItemType(module, obj, mdtvFolder, mdtvIndex, idGenerator, oldIdToNewIdMap, objectFactory);
		} else if ("FolderType" == objectType) {
			return ModuleUtils._copyFolderType(module, obj, mdtvFolder, mdtvIndex, idGenerator, oldIdToNewIdMap, objectFactory);
		}
		return null;
	}

	/**
	 * Copy a QuerySubject and insert it into the mdtvFolder.
	 * @param {Module} module
	 * @param {QuerySubject} obj
	 * @param {IdGenerator} idGenerator
	 * @param {Object} oldIdToNewIdMap
	 * @return {QuerySubject}  The copied QuerySubject
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static _copyQuerySubject(module, obj, mdtvFolder, mdtvIndex, idGenerator, oldIdToNewIdMap, objectFactory) {
		var qs = obj.clone();
		var oldId = qs.getIdentifier();
		idGenerator.generateId(qs);
		oldIdToNewIdMap[oldId] = qs.getIdentifier();
		PropertySetter.updateIdForExpression(qs.basicGetItem(), qs.getIdentifier());
		module.addQuerySubject(qs);
		ModuleUtils._insertFolderItem(mdtvFolder, qs, mdtvIndex, objectFactory);
		return qs;
	}

	/**
	 * Copy a Relationship.
	 * @param {Module} module
	 * @param {Relationship} obj
	 * @param {IdGenerator} idGenerator
	 * @param {Object} oldIdToNewIdMap
	 * @return {Relationship}  The copied Relationship
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static _copyRelationship(module, obj, idGenerator, oldIdToNewIdMap) {
		var rel = obj.clone();
		var qsId = oldIdToNewIdMap[rel.getLeft().getRef()];
		if (qsId != null) {
			rel.getLeft().setRef(qsId);
		}
		qsId = oldIdToNewIdMap[rel.getRight().getRef()];
		if (qsId != null) {
			rel.getRight().setRef(qsId);
		}
		idGenerator.generateId(rel);
		module.addRelationship(rel);
		ReferenceResolver.resolveRelationship(rel, module);
		return rel;
	}

	/**
	 * Copy a Calculation and insert it into the mdtvFolder.
	 * @param {Module} module
	 * @param {Calculation} obj
	 * @param {FolderType} mdtvFolder
	 * @param {int} mdtvIndex
	 * @param {IdGenerator} idGenerator
	 * @param {ObjectFactory} objectFactory
	 * @return {Calculation}  The copied Calculation
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static _copyCalculation(module, obj, mdtvFolder, mdtvIndex, idGenerator, objectFactory) {
		var calc = obj.clone();
		idGenerator.generateId(calc);
		module.addCalculation(calc);
		ModuleUtils._insertFolderItem(mdtvFolder, calc, mdtvIndex, objectFactory);
		return calc;
	}

	/**
	 * Copy a Filter and insert it into the mdtvFolder.
	 * @param {Module} module
	 * @param {Filter} obj
	 * @param {FolderType} mdtvFolder
	 * @param {int} mdtvIndex
	 * @param {IdGenerator} idGenerator
	 * @param {ObjectFactory} objectFactory
	 * @return {Filter}  The copied Filter
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static _copyFilter(module, obj, mdtvFolder, mdtvIndex, idGenerator, objectFactory) {
		var filter = obj.clone();
		idGenerator.generateId(filter);
		module.addFilter(filter);
		ModuleUtils._insertFolderItem(mdtvFolder, filter, mdtvIndex, objectFactory);
		return filter;
	}

	/**
	 * Copy a FolderItemType's content and insert it into the mdtvFolder.
	 * @param {Module} module
	 * @param {FolderItemType} obj
	 * @param {FolderType} mdtvFolder
	 * @param {int} mdtvIndex
	 * @param {IdGenerator} idGenerator
	 * @param {Object} oldIdToNewIdMap
	 * @param {ObjectFactory} objectFactory
	 * @return {MoserObject}  The nested MoserObject that was copied
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static _copyFolderItemType(module, obj, mdtvFolder, mdtvIndex, idGenerator, oldIdToNewIdMap, objectFactory) {
		var folder = obj.getFolder();
		var o = obj.getReferencedObject();
		if (folder) {
			return ModuleUtils._copyMoserObject(module, folder, mdtvFolder, mdtvIndex, idGenerator, oldIdToNewIdMap, objectFactory);
		} else if (o) {
			return ModuleUtils._copyMoserObject(module, o, mdtvFolder, mdtvIndex, idGenerator, oldIdToNewIdMap, objectFactory);
		}
		return null;
	}

	/**
	 * Copy a FolderType and insert it into the mdtvFolder. Also copy and insert all this folder's items into the new copy.
	 * @param {Module} module
	 * @param {FolderType} obj
	 * @param {FolderType} mdtvFolder
	 * @param {int} mdtvIndex
	 * @param {IdGenerator} idGenerator
	 * @param {Object} oldIdToNewIdMap
	 * @param {ObjectFactory} objectFactory
	 * @return {FolderType}  The copied FolderType
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static _copyFolderType(module, obj, mdtvFolder, mdtvIndex, idGenerator, oldIdToNewIdMap, objectFactory) {
		var folder = obj.clone();
		idGenerator.generateId(folder);
		var items = folder.basicGetFolderItem();
		if (items && !(items.length==0)) {
			var folderItems = [];
			Array.prototype.push.apply(folderItems, items);
			items.length = 0;
			var folderIndex = 0;
			for (var __i_enFor0 = 0, __exp_enFor0 = folderItems, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var item = __exp_enFor0[__i_enFor0];
				ModuleUtils._copyMoserObject(module, item, folder, folderIndex++, idGenerator, oldIdToNewIdMap, objectFactory);
			}
		}
		ModuleUtils._insertFolder(mdtvFolder, folder, mdtvIndex, objectFactory);
		return folder;
	}

	/**
	 * Collection relationships related to all QuerySubject's referenced in the oldIdToNewIdMap.
	 * @param  Module
	 * @param {Map} oldIdToNewIdMap
	 * @param {CopyMode} mode
	 * @return {Array}
	 */
	static _collectRelationshipsToCopy(module, oldIdToNewIdMap, mode) {
		var relationshipsToCopy = [];
		var relationships = module.basicGetRelationship();
		if (CopyMode.SELECTION_ONLY === mode || !relationships || relationships.length==0) {
			return relationshipsToCopy;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = relationships, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var relationship = __exp_enFor0[__i_enFor0];
			var isLeft = oldIdToNewIdMap[relationship.getLeft().getRef()] != null;
			var isRight = oldIdToNewIdMap[relationship.getRight().getRef()] != null;
			if (CopyMode.COMMON_RELATIONSHIPS === mode && isLeft && isRight) {
				relationshipsToCopy.push(relationship);
			} else if (CopyMode.ALL_RELATIONSHIPS === mode && (isLeft || isRight)) {
				relationshipsToCopy.push(relationship);
			}
		}
		return relationshipsToCopy;
	}

	/**
	 * Find query subject by original ref if it exists, otherwise, by ref
	 * @param {Module} module
	 * @param {String} ref
	 * @return {QuerySubject}  the matching query subject
	 */
	static _findQuerySubjectByOriginalRefThenRef(module, ref) {
		var qsList = MoserObjectUtils.getList(module.basicGetQuerySubject());
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			var refList = qs.basicGetOriginalRef();
			if (!refList || refList.length==0) {
				refList = qs.basicGetRef();
			}
			if (ModuleUtils._findString(refList, ref)) {
				return qs;
			}
		}
		return null;
	}

	static _updateExistingQuerySubject(existingQS, srcQS, objectFactory) {
		if (srcQS.basicGetItem()) {
			var qiMap = ModuleUtils._createQIMap(existingQS);
			var idForExprPrefix = existingQS.getIdentifier() + ".";
			for (var __i_enFor0 = 0, __exp_enFor0 = MoserObjectUtils.getQueryItems(srcQS), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qi = __exp_enFor0[__i_enFor0];
				if (!qiMap[qi.getIdentifier()]) {
					var newQI = qi.clone();
					newQI.setExpression(newQI.getIdentifier());
					newQI.setIdForExpression(idForExprPrefix + newQI.getIdentifier());
					var newItem = objectFactory.createItemType();
					newItem.setQueryItem(newQI);
					existingQS.addItem(newItem);
				}
			}
		}
	}

	static _createQIMap(qs) {
		var qiMap = {};
		if (qs.basicGetItem()) {
			for (var __i_enFor0 = 0, __exp_enFor0 = MoserObjectUtils.getQueryItems(qs), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qi = __exp_enFor0[__i_enFor0];
				qiMap[qi.getIdentifier()] = qi;
			}
		}
		return qiMap;
	}

	/**
	 * Insert a package into a module. This method copies the querysubjects, calculations, filters, namedsets into the module, and MDTV. It also updates the deflink ref and querysubject id to have a prefix (eg., M1)
	 * @param {ObjectFactory} objectFactory  : to create new objects
	 * @param {Module} module  : the destination module
	 * @param {String} srcModuleId  : the identifier of the package
	 * @param {Module} srcModule  : the package
	 * @param {FolderType} mdtvFolder  : the folder to insert in the MDTV
	 * @param {int} insertAtIndex  : the index of mdtvFolder array to indicate where to insert in MDTV
	 * @throws {CloneNotSupportedException} CloneNotSupportedException  : exception when cloning
	 */
	static insertPackage(objectFactory, module, srcModuleId, srcModule, mdtvFolder, insertAtIndex) {
		if (CommonResolverUtil.getFMPlaceHolderInMDTV(module, srcModuleId)) {
			return;
		}
		var qsList = MoserObjectUtils.getList(srcModule.basicGetQuerySubject());
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			var qsCopy = ModuleUtils._cloneAndUpdateId(qs, srcModuleId);
			module.addQuerySubject(qsCopy);
			for (var __i_enFor1 = 0, __exp_enFor1 = MoserObjectUtils.getQueryItems(qsCopy), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var qi = __exp_enFor1[__i_enFor1];
				qi.setIdForExpression(null);
			}
		}
		var calcList = MoserObjectUtils.getList(srcModule.basicGetCalculation());
		for (var __i_enFor2 = 0, __exp_enFor2 = calcList, __len_enFor2 = __exp_enFor2.length;
				__i_enFor2 < __len_enFor2; ++__i_enFor2) {
			var calc = __exp_enFor2[__i_enFor2];
			var calcCopy = ModuleUtils._cloneAndUpdateId(calc, srcModuleId);
			module.addCalculation(calcCopy);
		}
		var filterList = MoserObjectUtils.getList(srcModule.basicGetFilter());
		for (var __i_enFor3 = 0, __exp_enFor3 = filterList, __len_enFor3 = __exp_enFor3.length;
				__i_enFor3 < __len_enFor3; ++__i_enFor3) {
			var filter = __exp_enFor3[__i_enFor3];
			var filterCopy = ModuleUtils._cloneAndUpdateId(filter, srcModuleId);
			module.addFilter(filterCopy);
		}
		var namedSetList = MoserObjectUtils.getList(srcModule.basicGetNamedSet());
		for (var __i_enFor4 = 0, __exp_enFor4 = namedSetList, __len_enFor4 = __exp_enFor4.length;
				__i_enFor4 < __len_enFor4; ++__i_enFor4) {
			var namedSet = __exp_enFor4[__i_enFor4];
			var namedSetCopy = ModuleUtils._cloneAndUpdateId(namedSet, srcModuleId);
			module.addNamedSet(namedSetCopy);
		}
		var placeHolder = objectFactory.createFolderType();
		placeHolder.setIdentifier(srcModuleId);
		placeHolder.setLabel(srcModule.getLabel());
		var ft = srcModule.getMetadataTreeView()[0];
		var fItems = ft.getFolderItem();
		for (var __i_enFor5 = 0, __exp_enFor5 = fItems, __len_enFor5 = __exp_enFor5.length;
				__i_enFor5 < __len_enFor5; ++__i_enFor5) {
			var fit = __exp_enFor5[__i_enFor5];
			placeHolder.addFolderItem(fit);
		}		
		var packageFolderItem = objectFactory.createFolderItemType();
		packageFolderItem.setFolder(placeHolder);
		mdtvFolder.addFolderItemAt(insertAtIndex, packageFolderItem);
		ModuleUtils._updateFolderTypeReferences(placeHolder, srcModuleId);
		ReferenceResolver.resolve(module);
	}

	static _cloneAndUpdateId(obj, idPrefix) {
		var id = idPrefix + "." + obj.getIdentifier();
		var objCopy = obj.clone();
		objCopy.setIdentifier(id);
		objCopy.setIdForExpression(null);
		return objCopy;
	}

	static _updateFolderTypeReferences(folderType, idPrefix) {
		var folderItemTypes = folderType.basicGetFolderItem();
		if (folderItemTypes) {
			for (var __i_enFor0 = 0, __exp_enFor0 = folderItemTypes, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var folderItemType = __exp_enFor0[__i_enFor0];
				var nestedFolder = folderItemType.getFolder();
				if (nestedFolder) {
					ModuleUtils._updateFolderTypeReferences(nestedFolder, idPrefix);
				}
				var defLinkType = folderItemType.getDefLink();
				if (defLinkType && defLinkType.getIdentifier() != null) {
					var ref = idPrefix + "." + defLinkType.getIdentifier();
					defLinkType.setRef(ref);
				}
			}
		}
	}

	static _insertFolderItem(folder, obj, index, objectFactory) {
		var item = objectFactory.createFolderItemType();
		item.setRef(obj.getIdentifier());
		item.setReferencedObject(obj);
		folder.addFolderItemAt(index, item);
	}

	static _insertFolder(folder, obj, index, objectFactory) {
		var item = objectFactory.createFolderItemType();
		item.setFolder(obj);
		item.setReferencedObject(obj);
		folder.addFolderItemAt(index, item);
	}

	static _updateQuerySubjectRef(module, qs, srcModuleId, srcModule, isReference) {
		if (isReference) {
			var refs = qs.getOriginalRef();
			refs.length = 0;
			var ref = srcModuleId + "." + qs.getIdentifier();
			refs.push(ref);
			refs = qs.getRef();
			for (var i = 0; i < refs.length; ++i) {
				ref = refs[i];
				var srcModuleUseSpecId = ReferenceResolver.getFirstPart(ref);
				var oldUseSpec = QuerySubjectUtils.getUseSpecById(srcModule, srcModuleUseSpecId);
				if (!oldUseSpec) {
					continue;
				}
				var useSpec = ModuleUtils._getUseSpecByStoreId(module, oldUseSpec.getStoreID());
				var newModuleId;
				if (!useSpec) {
					useSpec = oldUseSpec.clone();
					newModuleId = IdGenerator.generateNewUseSpecIdWithPrefix(module, "__Base");
					useSpec.setIdentifier(newModuleId);
					module.addUseSpec(useSpec);
				} else {
					newModuleId = useSpec.getIdentifier();
				}
				var sepIndex = ref.indexOf(".");
				ref = newModuleId + "." + ref.substring(sepIndex + 1);
				refs[i] = ref;
			}
		} else {
			var refs = qs.basicGetOriginalRef();
			if (refs) {
				refs.length = 0;
			}
			refs = qs.getRef();
			refs.length = 0;
			refs.push(srcModuleId + "." + qs.getIdentifier());
		}
	}

	static _getUseSpecByStoreId(module, storeId) {
		var uSpecs = MoserObjectUtils.getList(module.basicGetUseSpec());
		for (var __i_enFor0 = 0, __exp_enFor0 = uSpecs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var useSpec = __exp_enFor0[__i_enFor0];
			if (storeId == useSpec.getStoreID()) {
				return useSpec;
			}
		}
		return null;
	}

	static _findRelationshipMatch(module, rel) {
		var relationShips = MoserObjectUtils.getList(module.basicGetRelationship());
		for (var __i_enFor0 = 0, __exp_enFor0 = relationShips, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var relshp = __exp_enFor0[__i_enFor0];
			if (RelationshipUtils.isEqual(rel, relshp)) {
				return relshp;
			}
		}
		return null;
	}

	static _findQuerySubjectByRef(module, qsRef) {
		var qsList = MoserObjectUtils.getList(module.basicGetQuerySubject());
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			if (ModuleUtils._findString(qs.basicGetRef(), qsRef) || ModuleUtils._findString(qs.basicGetOriginalRef(), qsRef)) {
				return qs;
			}
		}
		return null;
	}

	static _findString(strList, strValue) {
		if (!strList) {
			return false;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = strList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var str = __exp_enFor0[__i_enFor0];
			if (str == strValue) {
				return true;
			}
		}
		return false;
	}

	static _deleteRelationshipsForQuerySubject(module, rels, qsId) {
		var i;
		for (i = rels.length - 1; i >= 0; --i) {
			var rel = rels[i];
			if (qsId == rel.getLeft().getRef() || qsId == rel.getRight().getRef()) {
				module.removeRelationshipAt(i);
			}
		}
	}

	/**
	 * Delete a query subject from a module and clean up any reference to the query subject
	 * @param {Module} module
	 * @param {QuerySubject} qs  : query subject to be deleted from module
	 */
	static deleteQuerySubject(module, qs) {
		ModuleUtils._deleteQuerySubjectImp(module, qs, true);
	}

	static _deleteQuerySubjectRelatedObjects(module, qs, qsId) {
		var rels = MoserObjectUtils.getList(module.basicGetRelationship());
		ModuleUtils._deleteRelationshipsForQuerySubject(module, rels, qsId);
		var querySubjects = MoserObjectUtils.getList(module.basicGetQuerySubject());
		for (var __i_enFor0 = 0, __exp_enFor0 = querySubjects, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var aQS = __exp_enFor0[__i_enFor0];
			if (aQS === qs) {
				continue;
			}
			var join = aQS.getJoinOperation();
			if (!join) {
				continue;
			}
			var relsJoin = MoserObjectUtils.getList(join.basicGetRelationship());
			ModuleUtils._deleteRelationshipsForQuerySubject(module, relsJoin, qsId);
		}
		var dgs = MoserObjectUtils.getList(module.basicGetDrillGroup());
		var qsFirstPart = qsId + ".";
		var i;
		for (i = dgs.length - 1; i >= 0; --i) {
			var dg = dgs[i];
			var segs = dg.basicGetSegment();
			if (!segs) {
				continue;
			}
			for (var j = segs.length - 1; j >= 0; --j) {
				if (segs[j].getRef().indexOf(qsFirstPart) == 0) {
					dg.removeSegmentAt(j);
				}
			}
		}
	}

	static _deleteQuerySubjectImp(module, qs, bClean) {
		var qsId = qs.getIdentifier();
		if (bClean) {
			ModuleUtils._deleteQuerySubjectRelatedObjects(module, qs, qsId);
		}
		var mdtvs = MoserObjectUtils.getList(module.basicGetMetadataTreeView());
		for (var __i_enFor0 = 0, __exp_enFor0 = mdtvs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var folder = __exp_enFor0[__i_enFor0];
			var del = ModuleUtils._removeQuerySubjectFromMDTVFolder(qsId, folder, bClean);
			if (!bClean && del) {
				return del;
			}
		}
		module.removeQuerySubject(qs);
		return qs;
	}

	static _removeQuerySubjectFromMDTVFolder(qsId, folder, bClean) {
		if (!folder) {
			return null;
		}
		var itemList = MoserObjectUtils.getList(folder.basicGetFolderItem());
		for (var j = itemList.length - 1; j >= 0; --j) {
			if (qsId == itemList[j].getRef()) {
				var r = itemList[j];
				if (bClean) {
					folder.removeFolderItemAt(j);
				} else {
					return r;
				}
			} else {
				var r = ModuleUtils._removeQuerySubjectFromMDTVFolder(qsId, itemList[j].getFolder(), bClean);
				if (r && !bClean) {
					return r;
				}
			}
		}
		return null;
	}

	/**
	 * We are inserting qsList into dstModule, so we need to find some relationships in srcModule that we can copy to dstModule as well
	 * @param  srcModule: Source Module
	 * @param  srcModuleId: this is dstModule.srcModuleUseSpec.id
	 * @param  qsList: List of query subjects coming from the srcModule and will be added to the dstModule
	 * @param  dstModule: Destination Module
	 * @return {Array}  List of suggested relationships
	 */
	static suggestRelationships(srcModule, srcModuleId, qsList, dstModule) {
		var result = [];
		var qsIds = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			qsIds[qs.getIdentifier()] = 1;
		}
		var qsRefs = {};
		var dstQSList = MoserObjectUtils.getList(dstModule.basicGetQuerySubject());
		for (var __i_enFor1 = 0, __exp_enFor1 = dstQSList, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var qs = __exp_enFor1[__i_enFor1];
			var prefix = srcModuleId + ".";
			var qsRefList = MoserObjectUtils.getList(qs.basicGetRef());
			for (var __i_enFor2 = 0, __exp_enFor2 = qsRefList, __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var ref = __exp_enFor2[__i_enFor2];
				if (ref.indexOf(prefix) == 0) {
					qsRefs[ref] = 1;
				}
			}
			var qsOrgRefList = MoserObjectUtils.getList(qs.basicGetOriginalRef());
			for (var __i_enFor3 = 0, __exp_enFor3 = qsOrgRefList, __len_enFor3 = __exp_enFor3.length;
					__i_enFor3 < __len_enFor3; ++__i_enFor3) {
				var ref = __exp_enFor3[__i_enFor3];
				if (ref.indexOf(prefix) == 0) {
					qsRefs[ref] = 1;
				}
			}
		}
		var srcRelList = MoserObjectUtils.getList(srcModule.basicGetRelationship());
		for (var __i_enFor4 = 0, __exp_enFor4 = srcRelList, __len_enFor4 = __exp_enFor4.length;
				__i_enFor4 < __len_enFor4; ++__i_enFor4) {
			var rel = __exp_enFor4[__i_enFor4];
			var otherQSId;
			if (qsIds.hasOwnProperty(rel.getLeft().getRef())) {
				otherQSId = rel.getRight().getRef();
			} else if (qsIds.hasOwnProperty(rel.getRight().getRef())) {
				otherQSId = rel.getLeft().getRef();
			} else {
				continue;
			}
			if (qsIds.hasOwnProperty(otherQSId)) {
				result.push(rel);
			} else {
				var otherRef = srcModuleId + "." + otherQSId;
				if (qsRefs.hasOwnProperty(otherRef)) {
					result.push(rel);
				} else {
					var qs = ModuleUtils.getQuerySubjectById(otherQSId, srcModule);
					if (qs) {
						var ref = qs.getRef()[0];
						if (ref != null && !(ref.length === 0)) {
							var useSpec = DataModuleExtractor.getUseSpecByRef(srcModule, ref, true);
							var storeId = null;
							if (useSpec) {
								storeId = useSpec.getStoreID();
							}
							if (storeId != null) {
								var dstModuleUSpecs = MoserObjectUtils.getList(dstModule.basicGetUseSpec());
								for (var __i_enFor5 = 0, __exp_enFor5 = dstModuleUSpecs, __len_enFor5 = __exp_enFor5.length;
										__i_enFor5 < __len_enFor5; ++__i_enFor5) {
									var us = __exp_enFor5[__i_enFor5];
									var id = us.getIdentifier();
									if (us.getStoreID() == storeId && id.indexOf("__Base") != 0) {
										var dstRef = us.getIdentifier() + "." + ReferenceResolver.getSecondPart(ref);
										var qsDst = ModuleUtils._findQuerySubjectByRef(dstModule, dstRef);
										if (qsDst) {
											result.push(rel);
										}
										break;
									}
								}
							}
						}
					}
				}
			}
		}
		return result;
	}

	/**
	 * Retrieve <code>QuerySubject</code> by identifier
	 * @param {String} id 	the identifier used as a 'ref' by other objects
	 * @param {Module} module 	the data module
	 * @return {QuerySubject}  a reference of the <code>QuerySubject</code>
	 */
	static getQuerySubjectById(id, module) {
		var querySubject;
		var qsList = MoserObjectUtils.getList(module.basicGetQuerySubject());
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			if (qs.getIdentifier() == id) {
				querySubject = qs;
				break;
			}
		}
		return querySubject;
	}

	/**
	 * Retrieve <code>Calculation</code> by identifier
	 * @param {String} id  the identifier used as a 'ref' by other objects
	 * @param {Module} module 	the data module
	 * @return {Calculation}  a reference of the <code>Calculation</code>
	 */
	static getCalculationById(id, module) {
		var calculation;
		var calculations = MoserObjectUtils.getList(module.basicGetCalculation());
		for (var __i_enFor0 = 0, __exp_enFor0 = calculations, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var calc = __exp_enFor0[__i_enFor0];
			if (calc.getIdentifier() == id) {
				calculation = calc;
				break;
			}
		}
		return calculation;
	}

	/**
	 * Retrieve <code>QueryItem</code> by identifier
	 * @param {String} qualifiedID  the identifier used as a 'ref' by other objects to refer to a QueryItem. It is in the format of "querysubjectid.queryitemid"
	 * @param {Module} module 	the data module
	 * @return {BaseQueryItemType}  a reference of the <code>QueryItem</code>
	 */
	static getQueryItemById(qualifiedID, module) {
		var queryItem;
		var qsId = ReferenceResolver.getFirstPart(qualifiedID);
		if (qsId != null && qsId.length > 0) {
			var qs = ModuleUtils.getQuerySubjectById(qsId, module);
			var qiId = ReferenceResolver.getSecondPart(qualifiedID);
			if (qs && qiId != null && qiId.length > 0) {
				queryItem = QuerySubjectUtils.getQueryItemById(qiId, qs);
			}
		}
		return queryItem;
	}

	/**
	 * Retrieve <code>Filter</code> by identifier
	 * @param {String} id  the identifier used as a 'ref' by other objects
	 * @param {Module} module 	the data module
	 * @return {Filter}  a reference of the <code>Filter</code>
	 */
	static getFilterById(id, module) {
		var filter;
		var filterList = MoserObjectUtils.getList(module.basicGetFilter());
		for (var __i_enFor0 = 0, __exp_enFor0 = filterList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var fltr = __exp_enFor0[__i_enFor0];
			if (fltr.getIdentifier() == id) {
				filter = fltr;
				break;
			}
		}
		return filter;
	}

	/**
	 * Retrieve <code>MoserObject</code> by idForExpression
	 * @param {String} idForExprn  the idForExpression to look for
	 * @param {Module} module 	the data module
	 * @return {MoserObject}  a reference of the <code>MoserObject</code> including QuerySubject, QueryItem, Calculation and Filter
	 */
	static getMoserObjectByIdForExpression(idForExprn, module) {
		var obj = ReferenceResolver.getMoserObjectByIdForExpression(idForExprn, module);
		if (!obj) {
			var alias = ReferenceResolver.strBetween(idForExprn, "[", "].");
			if (alias != null) {
				for (var __i_enFor0 = 0, __exp_enFor0 = module.getUseSpec(), __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var useSpec = __exp_enFor0[__i_enFor0];
					if (alias == useSpec.getIdentifier()) {
						var newID = alias + "." + ReferenceResolver.strAfter(idForExprn, "].");
						return ReferenceResolver.getMoserObjectByIdForExpression(newID, module);
					}
				}
			}
		}
		return obj;
	}

	static _getModule(o) {
		var obj = o.getParent();
		while (obj) {
			if ("Module" == obj.getObjectType()) {
				return obj;
			}
			obj = obj.getParent();
		}
		return null;
	}

	/**
	 * If you have a ObjectType, it may have a ref that point to an object in a source. This method returns the UseSpec that point to that source.
	 * @param {ObjectType} obj  : ObjectType to find UseSpec for.
	 * @param {Module} m  : the module that contains the object. if null, we'll try to find it using getParent()
	 * @return {UseSpec}  UseSpec of the referenced source or null if can't find it.
	 */
	static getObjectUseSpec(obj, m) {
		var module = m;
		if (!module) {
			module = ModuleUtils._getModule(obj);
			if (!module) {
				throw new Error("Can't find the module!");
			}
		}
		return DataModuleExtractor.getUseSpec(obj, module, false);
	}

	/**
	 * For a given UseSpec, return the list of ObjectType's in the Module that are referencing it. The only ObjectType's that can have a reference are: QuerySubject, Relationship, Calculation, Filter
	 * @param {UseSpec} useSpec  : The UseSpec to find all references of
	 * @param {Module} m  : the module that contains the object. if null, we'll try to find it using getParent()
	 * @return {Array}
	 */
	static getUseSpecRefs(useSpec, m) {
		var module = m;
		if (!module) {
			module = ModuleUtils._getModule(useSpec);
			if (!module) {
				throw new Error("Can't find the module!");
			}
		}
		return DataModuleExtractor.getUseSpecRefs(useSpec, module);
	}

	/**
	 * Create a list of Ancestors by parsing a JSONArray that is returned with /sources calls.
	 * @param {Array} ancestorsResp JSONArray that is returned with /sources calls., e.g.[ { "name": "Some directory", "id": "iAA9C873CD3AF42D5ADB36572D05C98E6", "type": "directory" }, { "name": "My Folders", "id": "i32DE242D91604B8DAB9CE8EA5E46B37D", "type": "folder" }, { "name": "Some directory", "id": "iAA9C873CD3AF42D5ADB36572D05C98E6", "type": "directory" } ]
	 * @param {String} myFoldersLabel  symbolic id for my content folder
	 * @param {String} teamContentLabel  symbolic if for team content folder
	 * @return {Array}  list of ancestors
	 */
	static createAncestors(ancestorsResp, myFoldersLabel, teamContentLabel) {
		return MoserCommonUtils.createAncestors(ancestorsResp, myFoldersLabel, teamContentLabel);
	}

	/**
	 * Create members under the given parent from the JSON response to calling the members endpoint API.
	 * @param {ObjectFactory} factory  - Used to create a member.
	 * @param {MoserObject} parent  - Created members are added to this parent.
	 * @param {Object} membersResponse  - Input members response from which members are created.
	 */
	static createMembers(factory, parent, membersResponse) {
		if (!factory || !parent || !membersResponse) {
			return null;
		}
		var dataItems = membersResponse["dataItems"];
		if (!dataItems || dataItems.length==0) {
			return null;
		}
		if (!(parent instanceof QueryItem || parent instanceof MemberType)) {
			return null;
		}
		var items = (dataItems[0])["items"];
		if (!items) {
			return null;
		}
		var members = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var member = ModuleUtils._addMember(factory, ModuleUtils._getData(item), parent);
			if (member) {
				members.push(member);
				ModuleUtils.setMemberHidden(member, false);
			}
		}
		return members;
	}

	static _getData(item) {
		var t = item["t"];
		if (!t || t.length != 1) {
			return null;
		}
		return t[0];
	}

	/**
	 * @param {ObjectFactory} factory
	 * @param  item
	 * @param {MoserObject} parent
	 */
	static _addMember(factory, data, parent) {
		if (!data || !parent) {
			return null;
		}
		var mun = data["u"];
		var member = ModuleUtils._getMember(parent, mun);
		if (member) {
			if (parent instanceof QueryItem) {
				(parent).removeMembers(member);
				(parent).addMembers(member);
			} else {
				(parent).removeMembers(member);
				(parent).addMembers(member);
			}
			return member;
		}
		member = factory.createMemberType();
		ModuleUtils.setMemberHidden(member, true);
		member.setIdentifier(mun);
		member.setIdForExpression(mun);
		member.setLabel(data["d"]);
		member.setMemberCaption(data["d"]);
		var jparent = data["p"];
		if (jparent) {
			member.setParentUniqueName(jparent["u"]);
		}
		member.setDimensionUniqueName(data["du"]);
		member.setLevelNumber(data["ln"]);
		member.setLevelLabel(data["ll"]);
		member.setLevelUniqueName(data["lu"]);
		member.setHierarchyUniqueName(data["hu"]);
		member.setParentLevel(data["pn"]);
		if (parent instanceof QueryItem) {
			(parent).addMembers(member);
		} else {
			(parent).addMembers(member);
		}
		return member;
	}

	/**
	 * @param {MoserObject} parent
	 * @param {String} mun
	 * @return {MemberType}
	 */
	static _getMember(parent, mun) {
		if (mun == null) {
			return null;
		}
		var members;
		if (parent instanceof QueryItem) {
			members = (parent).basicGetMembers();
		} else {
			members = (parent).basicGetMembers();
		}
		if (!members || members.length==0) {
			return null;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = members, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var member = __exp_enFor0[__i_enFor0];
			if (mun == member.getIdForExpression()) {
				return member;
			}
		}
		return null;
	}

	/**
	 * Create members under the given parent from the JSON response to calling the members endpoint API.
	 * @param {ObjectFactory} factory  - Used to create a member.
	 * @param  parent  - Created members are added to this parent.
	 * @param {Object} membersResponse  - Input members response from which members are created.
	 */
	static createMembersForSearch(factory, module, membersResponse) {
		if (!factory || !module || !membersResponse) {
			return null;
		}
		var dataItems = membersResponse["dataItems"];
		if (!dataItems || dataItems.length != 1) {
			return null;
		}
		var items = (dataItems[0])["items"];
		if (!items || items.length==0) {
			return null;
		}
		var members = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var data = ModuleUtils._getData(item);
			var parent = ModuleUtils._getMemberParent(module, data);
			if (parent) {
				var member = ModuleUtils._addMember(factory, data, parent);
				if (member) {
					members.push(member);
					member.setParent(parent);
				}
			}
		}
		return members;
	}

	/**
	 * @param {Module} module
	 * @param {Object} item
	 * @return {MoserObject}
	 */
	static _getMemberParent(module, item) {
		if (!module || !item) {
			return null;
		}
		var mun = item["u"];
		if (mun == null) {
			return null;
		}
		var itemId = ModuleUtils._extractItemID(mun);
		return ReferenceResolver.bindMoserObject(module, itemId);
	}

	/**
	 * @param {String} startAtPath
	 * @return {String}
	 */
	static _extractItemID(startAtPath) {
		var itemId = startAtPath;
		var idx = startAtPath.indexOf("->");
		if (idx != -1) {
			itemId = startAtPath.substring(0, idx);
		}
		return itemId;
	}

	/**
	 * Given the Moser JSON response of a long running task (TaskState), calculate a percentage completed.
	 * @param {Object} taskStatus
	 */
	static taskPercentage(taskStatus) {
		var state = taskStatus["state"];
		if ("SUCCESS" == state || "CANCELLED" == state || "ERROR" == state || "NOT_AVAILABLE" == state) {
			return 100;
		}
		if ("PENDING" == state) {
			return 0;
		}
		return ModuleUtils._taskStepPercentage(taskStatus["taskStep"]);
	}

	/**
	 * Given the JSON of a TaskStep, calculate a percentage completed.
	 * @param {Object} taskStep
	 */
	static _taskStepPercentage(taskStep) {
		var numTotalSteps = (taskStep["numTotalSteps"]);
		var numCompleted = taskStep.hasOwnProperty("numCompleted") ? (taskStep["numCompleted"]) : 0;
		var currentStepNum = (taskStep["currentStepNum"]);
		var percentagePerStep = 100.0 / numTotalSteps;
		var stepNum = currentStepNum < 1 ? 1 : currentStepNum;
		stepNum += numCompleted;
		var percentageCompleted = percentagePerStep * (stepNum - 1);
		var currentSubTask = taskStep["currentSubTask"];
		if (currentSubTask) {
			percentageCompleted += percentagePerStep * ModuleUtils._taskStepPercentage(currentSubTask) / 100.0;
		}
		return percentageCompleted > 100.0 ? 100 : percentageCompleted < 0.0 ? 0 : Math.round(percentageCompleted);
	}

	static findObjectTypeByIdForExpression(module, idForExprn) {
		if (idForExprn == null || idForExprn.length === 0) {
			return null;
		}
		var parts = idForExprn.split(new RegExp("\\."));
		if (parts.length == 0) {
			return null;
		}
		if (parts.length == 1) {
			var qs = ModuleUtils.getQuerySubjectById(parts[0], module);
			if (qs) {
				return qs;
			}
			var calc = ModuleUtils.getCalculationById(parts[0], module);
			if (calc) {
				return calc;
			}
			return ModuleUtils.getFilterById(parts[0], module);
		}
		var qs = ModuleUtils.getQuerySubjectById(parts[0], module);
		if (!qs) {
			return null;
		}
		var items = qs.basicGetItem();
		var rt;
		for (var currentPos = 1; currentPos < parts.length; ++currentPos) {
			rt = ModuleUtils._findObjectTypeByIdInItems(items, parts[currentPos]);
			if (!rt) {
				break;
			}
			if (rt.getObjectType() == "QueryItem") {
				items = (rt).basicGetItem();
			} else {
				items = null;
			}
		}
		return rt;
	}

	static _findObjectTypeByIdInItems(items, id) {
		if (!items) {
			return null;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			var qi = itm.getQueryItem();
			if (qi) {
				if (id == qi.getIdentifier()) {
					return qi;
				}
				continue;
			}
			var flt = itm.getFilter();
			if (flt) {
				if (id == flt.getIdentifier()) {
					return flt;
				}
				continue;
			}
			var folder = itm.getFolder();
			if (folder) {
				var rt = ModuleUtils._findObjectTypeByIdInItems(folder.basicGetItem(), id);
				if (rt) {
					return rt;
				}
				continue;
			}
		}
		return null;
	}

	static areCompatibleDataTypes(lDataType, rDataType) {
		return MoserCommonUtils.areCompatibleDataTypes(lDataType, rDataType);
	}

	static getPrecision(dataType) {
		return MoserCommonUtils.getPrecision(dataType);
	}

	static getScale(dataType) {
		return MoserCommonUtils.getScale(dataType);
	}

	/**
	 * (item collection)				itemType	itemType	itemType	itemType		itemType	itemType querysubject 	queryitem	namedset	filter		itemHierarchy	folder		calculation queryitem		y				y			x			x			x				?(qs or qi)	x namedset		y				x			x			x			x				?(qs)		x filter			y				y			x			x			x				?(qs)		x itemHierarchy	x				x			x			x			x				x			x folder			y				y			x			x			x				y			y calculation		x				x			x			x			x				?(calc)		y security/ embedded filer  y(direct)		x			x			x			x				x			x in MDTV Querysubject statndalone Calculation statndalone Filter FolderType
	 * @return {boolean}  t/f.
	 */
	static _checkValidContainer(obj, parentObj, bEmbeddedFilter) {
		if (!obj || !parentObj) {
			return false;
		}
		var objTp = obj.getObjectType();
		var allowed = {};
		var skipped = {};
		if ("QuerySubject" == objTp) {
			allowed["Module"] = 1;
			allowed["FolderType"] = 1;
			skipped["FolderItemType"] = 1;
			return ModuleUtils._checkContainer(parentObj, allowed, skipped);
		}
		if ("FolderType" == objTp) {
			allowed["Module"] = 1;
			allowed["FolderType"] = 1;
			skipped["FolderItemType"] = 1;
			return ModuleUtils._checkContainer(parentObj, allowed, skipped);
		}
		if ("QueryItem" == objTp) {
			allowed["QuerySubject"] = 1;
			allowed["QueryItem"] = 1;
			skipped["Folder"] = 1;
			skipped["ItemType"] = 1;
			return ModuleUtils._checkContainer(parentObj, allowed, skipped);
		}
		if ("NamedSet" == objTp) {
			allowed["QuerySubject"] = 1;
			skipped["Folder"] = 1;
			skipped["ItemType"] = 1;
			return ModuleUtils._checkContainer(parentObj, allowed, skipped);
		}
		if ("Filter" == objTp) {
			if (bEmbeddedFilter) {
				allowed["QuerySubject"] = 1;
				return ModuleUtils._checkContainer(parentObj, allowed, skipped);
			}
			var qs = ModuleUtils._getParentQuerySubject(parentObj);
			if (qs) {
				allowed["QuerySubject"] = 1;
				allowed["QueryItem"] = 1;
				skipped["Folder"] = 1;
				skipped["ItemType"] = 1;
				return ModuleUtils._checkContainer(parentObj, allowed, skipped);
			}
			allowed["Module"] = 1;
			allowed["FolderType"] = 1;
			skipped["FolderItemType"] = 1;
			return ModuleUtils._checkContainer(parentObj, allowed, skipped);
		}
		if ("Folder" == objTp) {
			allowed["QuerySubject"] = 1;
			allowed["QueryItem"] = 1;
			allowed["Folder"] = 1;
			allowed["Calculation"] = 1;
			skipped["ItemType"] = 1;
			return ModuleUtils._checkContainer(parentObj, allowed, skipped);
		}
		if ("Calculation" == objTp) {
			allowed["Module"] = 1;
			allowed["FolderType"] = 1;
			allowed["Calculation"] = 1;
			skipped["Folder"] = 1;
			skipped["ItemType"] = 1;
			skipped["FolderItemType"] = 1;
			return ModuleUtils._checkContainer(parentObj, allowed, skipped);
		}
		if ("SecurityFilter" == objTp) {
			allowed["QuerySubject"] = 1;
			return ModuleUtils._checkContainer(parentObj, allowed, skipped);
		}
		return false;
	}

	static _checkContainer(parentObj, allowed, skipped) {
		var parent = parentObj;
		while (parent) {
			var parentTp = parent.getObjectType();
			if (allowed.hasOwnProperty(parentTp)) {
				return true;
			}
			if (!(skipped.hasOwnProperty(parentTp))) {
				return false;
			}
			parent = parent.getParent();
		}
		return false;
	}

	/**
	 * add an object to the parent object. supported obj classes: QueryItem			-> QuerySubject SecurityFilter		-> QuerySubject Folder				-> QuerySubject QuerySubject			-> Module Calculation			-> Module FolderType			-> Module Filter				-> QuerySubject or Module depending on parentObj
	 * @param {MoserObject} obj 				object to add.
	 * @param {MoserObject} parentObj 			parent object.
	 * @param {int} index 				position.
	 * @param {boolean} bEmbeddedFilter 	optional. only for filter inside query subject. If true, it is embedded filter.
	 * @param {ObjectFactory} objectFactory 		factory.
	 */
	static add(obj, parentObj, index, objectFactory, bEmbeddedFilter) {
		if (!(ModuleUtils._checkValidContainer(obj, parentObj, bEmbeddedFilter))) {
			return false;
		}
		var objTp = obj.getObjectType();
		if ("QueryItem" == objTp || "SecurityFilter" == objTp || "NamedSet" == objTp) {
			return ModuleUtils._addObjectToQuerySubject(obj, parentObj, index, objectFactory, false);
		}
		if ("Folder" == objTp) {
			var qs = ModuleUtils._getParentQuerySubject(parentObj);
			if (qs) {
				return ModuleUtils._addObjectToQuerySubject(obj, parentObj, index, objectFactory, false);
			}
			return ModuleUtils._addObjectToCalculation(obj, parentObj, index, objectFactory);
		}
		if ("Filter" == objTp) {
			var qs = ModuleUtils._getParentQuerySubject(parentObj);
			if (qs) {
				return ModuleUtils._addObjectToQuerySubject(obj, parentObj, index, objectFactory, bEmbeddedFilter);
			}
			return ModuleUtils._addObjectToModule(obj, parentObj, index, objectFactory);
		}
		if ("QuerySubject" == objTp) {
			return ModuleUtils._addObjectToModule(obj, parentObj, index, objectFactory);
		}
		if ("Calculation" == objTp) {
			var c = ModuleUtils._getParentCalculation(parentObj);
			if (c) {
				var module = MoserObject.getModuleObject(c);
				if (!module) {
					return false;
				}
				ModuleUtils._setIdentifierInModuleScope(obj, module);
				return ModuleUtils._addObjectToCalculation(obj, parentObj, index, objectFactory);
			}
			return ModuleUtils._addObjectToModule(obj, parentObj, index, objectFactory);
		}
		if ("FolderType" == objTp) {
			return ModuleUtils._addFolderTypeToMDTV(obj, parentObj, index, objectFactory);
		}
		return false;
	}

	static _getParentCalculation(parentObj) {
		var obj = parentObj;
		while (obj) {
			var tp = obj.getObjectType();
			if ("Calculation" == tp) {
				return obj;
			}
			if ("Module" == tp) {
				return null;
			}
			obj = obj.getParent();
		}
		return null;
	}

	static _getParentQuerySubject(parentObj) {
		var parentTp = parentObj.getObjectType();
		if ("QuerySubject" == parentTp) {
			return parentObj;
		}
		return PropertySetter.getParentQuerySubject(parentObj);
	}

	static _invalidIndex(list, index) {
		return !list || index < 0 || index >= list.length;
	}

	static _addItemToParent(item, parentObj, index) {
		var parentTp = parentObj.getObjectType();
		if ("QuerySubject" == parentTp) {
			if (ModuleUtils._invalidIndex((parentObj).basicGetItem(), index)) {
				(parentObj).addItem(item);
			} else {
				(parentObj).addItemAt(index, item);
			}
			return;
		}
		if ("QueryItem" == parentTp) {
			if (ModuleUtils._invalidIndex((parentObj).basicGetItem(), index)) {
				(parentObj).addItem(item);
			} else {
				(parentObj).addItemAt(index, item);
			}
			return;
		}
		if ("Folder" == parentTp) {
			if (ModuleUtils._invalidIndex((parentObj).basicGetItem(), index)) {
				(parentObj).addItem(item);
			} else {
				(parentObj).addItemAt(index, item);
			}
			return;
		}
		if ("Calculation" == parentTp) {
			if (ModuleUtils._invalidIndex((parentObj).basicGetItem(), index)) {
				(parentObj).addItem(item);
			} else {
				(parentObj).addItemAt(index, item);
			}
			return;
		}
	}

	static _getIdChain(idChain, parentObj, anchor) {
		var parent = parentObj;
		while (parent) {
			var objType = parent.getObjectType();
			if ("QuerySubject" == objType || "QueryItem" == objType) {
				idChain.push((parent).getIdentifier());
			}
			if (parent === anchor) {
				break;
			}
			parent = parent.getParent();
		}
	}

	static _addObjectToQuerySubject(obj, parentObj, index, objectFactory, bEmbeddedFilter) {
		var qs = ModuleUtils._getParentQuerySubject(parentObj);
		if (!qs) {
			return false;
		}
		var tp = obj.getObjectType();
		if (!("Folder" == tp)) {
			var label = (obj).getLabel();
			if (label == null || label.length === 0) {
				label = "new" + tp;
			}
			var id = (obj).getIdentifier();
			if (id == null || id.length === 0) {
				id = IdGenerator.makeIdentifier(parentObj, label);
				(obj).setIdentifier(id);
				var idChain = [];
				ModuleUtils._getIdChain(idChain, parentObj, qs);
				var idforexpr = "";
				for (var i = idChain.length - 1; i >= 0; --i) {
					idforexpr = idforexpr + idChain[i] + ".";
				}
				idforexpr = idforexpr + id;
				(obj).setIdForExpression(idforexpr);
			}
		}
		if ("QueryItem" == tp) {
			(obj).setHighlevelDatatype(MoserCommonUtils.generateHighlevelDatatype((obj).getDatatype()));
			var item = objectFactory.createItemType();
			item.setQueryItem(obj);
			ModuleUtils._addItemToParent(item, parentObj, index);
			PropertySetter.addQueryItemToItemNormalization(qs, obj, objectFactory);
			return true;
		}
		if ("NamedSet" == tp) {
			var item = objectFactory.createItemType();
			item.setNamedSet(obj);
			ModuleUtils._addItemToParent(item, parentObj, index);
			return true;
		}
		if ("Folder" == tp) {
			var item = objectFactory.createItemType();
			item.setFolder(obj);
			ModuleUtils._addItemToParent(item, parentObj, index);
			return true;
		}
		if ("Filter" == tp) {
			if (bEmbeddedFilter) {
				if (ModuleUtils._invalidIndex((parentObj).basicGetFilter(), index)) {
					(parentObj).addFilter(obj);
				} else {
					(parentObj).addFilterAt(index, obj);
				}
			} else {
				var item = objectFactory.createItemType();
				item.setFilter(obj);
				ModuleUtils._addItemToParent(item, parentObj, index);
			}
			return true;
		}
		if (ModuleUtils._invalidIndex((parentObj).basicGetSecurityFilter(), index)) {
			(parentObj).addSecurityFilter(obj);
		} else {
			(parentObj).addSecurityFilterAt(index, obj);
		}
		return true;
	}

	static _addObjectToCalculation(obj, parentObj, index, objectFactory) {
		var tp = obj.getObjectType();
		if ("Calculation" == tp) {
			var item = objectFactory.createItemType();
			item.setCalculation(obj);
			ModuleUtils._addItemToParent(item, parentObj, index);
			return true;
		}
		if ("Folder" == tp) {
			var item = objectFactory.createItemType();
			item.setFolder(obj);
			ModuleUtils._addItemToParent(item, parentObj, index);
			return true;
		}
		return false;
	}

	static _getParentFolderType(parentObj) {
		var parent = parentObj;
		while (parent) {
			var parentTp = parent.getObjectType();
			if ("FolderType" == parentTp) {
				return parent;
			}
			if ("Module" == parentTp) {
				var module = parent;
				var mdtv = module.basicGetMetadataTreeView();
				if (mdtv && !(mdtv.length==0)) {
					return mdtv[0];
				}
				return null;
			}
			parent = parent.getParent();
		}
		return null;
	}

	static _addFolderTypeToMDTV(obj, parentObj, index, objectFactory) {
		var parentFolderType = ModuleUtils._getParentFolderType(parentObj);
		if (!parentFolderType) {
			return false;
		}
		var folderItem = objectFactory.createFolderItemType();
		folderItem.setFolder(obj);
		if (ModuleUtils._invalidIndex(parentFolderType.basicGetFolderItem(), index)) {
			parentFolderType.addFolderItem(folderItem);
		} else {
			parentFolderType.addFolderItemAt(index, folderItem);
		}
		return true;
	}

	static _setIdentifierInModuleScope(obj, module) {
		var label = (obj).getLabel();
		if (label == null || label.length === 0) {
			label = "new" + obj.getObjectType();
		}
		var id = (obj).getIdentifier();
		if (id == null || id.length === 0) {
			id = IdGenerator.makeIdentifier(module, label);
			(obj).setIdentifier(id);
			(obj).setIdForExpression(id);
		}
		return id;
	}

	static _addObjectToModule(obj, parentObj, index, objectFactory) {
		var parentFolderType = ModuleUtils._getParentFolderType(parentObj);
		if (!parentFolderType) {
			return false;
		}
		var module = MoserObject.getModuleObject(parentFolderType);
		if (!module) {
			return false;
		}
		var id = ModuleUtils._setIdentifierInModuleScope(obj, module);
		var tp = obj.getObjectType();
		var folderItem = objectFactory.createFolderItemType();
		folderItem.setRef(id);
		folderItem.setReferencedObject(obj);
		if (ModuleUtils._invalidIndex(parentFolderType.basicGetFolderItem(), index)) {
			parentFolderType.addFolderItem(folderItem);
		} else {
			parentFolderType.addFolderItemAt(index, folderItem);
		}
		if ("QuerySubject" == tp) {
			module.addQuerySubject(obj);
		} else if ("Calculation" == tp) {
			module.addCalculation(obj);
		} else {
			module.addFilter(obj);
		}
		return true;
	}

	/**
	 * remove an object.
	 * @param {MoserObject} obj  object to remove.
	 * @return {MoserObject}  obj or null.
	 */
	static remove(obj) {
		return ModuleUtils._removeImp(obj, true);
	}

	static _removeImp(obj, bClean) {
		if (!obj) {
			return null;
		}
		var objTp = obj.getObjectType();
		if ("QueryItem" == objTp || "SecurityFilter" == objTp || "Folder" == objTp) {
			return ModuleUtils._removeObjectFromQuerySubject(obj, bClean);
		}
		if ("Filter" == objTp) {
			var qs = ModuleUtils._getParentQuerySubject(obj);
			if (qs) {
				return ModuleUtils._removeObjectFromQuerySubject(obj, bClean);
			}
			return ModuleUtils._removeObjectFromModule(obj, bClean);
		}
		if ("Calculation" == objTp || "QuerySubject" == objTp) {
			return ModuleUtils._removeObjectFromModule(obj, bClean);
		}
		if ("FolderType" == objTp) {
			return ModuleUtils._removeFolderTypeFromMDTV(obj, bClean);
		}
		return null;
	}

	static _removeObjectFromQuerySubject(obj, bClean) {
		var objTp = obj.getObjectType();
		if ("Folder" == objTp || "QueryItem" == objTp) {
			return ModuleUtils._removeParentItem(obj, bClean);
		} else if ("SecurityFilter" == objTp) {
			var parentTp = ModuleUtils._getParentObjectType(obj);
			if ("QuerySubject" == parentTp) {
				var qs = obj.getParent();
				if (bClean) {
					qs.removeSecurityFilter(obj);
				}
				return obj;
			}
			return null;
		} else if ("Filter" == objTp) {
			var parentTp = ModuleUtils._getParentObjectType(obj);
			if ("QuerySubject" == parentTp) {
				var qs = obj.getParent();
				if (bClean) {
					qs.removeFilter(obj);
				}
				return obj;
			}
			return ModuleUtils._removeParentItem(obj, bClean);
		} else {
			return null;
		}
	}

	static _getParentObjectType(obj) {
		var parent = obj.getParent();
		if (!parent) {
			return null;
		}
		return parent.getObjectType();
	}

	/**
	 * obj is in ItemType.
	 * @param {MoserObject} obj  object.
	 * @return {MoserObject}  obj or null.
	 */
	static _removeParentItem(obj, bclean) {
		var parentTp = ModuleUtils._getParentObjectType(obj);
		if (!("ItemType" == parentTp)) {
			return null;
		}
		var item = obj.getParent();
		var parentParent = item.getParent();
		if (!parentParent) {
			return null;
		}
		var parentParentTp = parentParent.getObjectType();
		var qiIds;
		var qiIdForExpressions;
		var qsId;
		var module;
		var qs;
		if (bclean) {
			qiIds = [];
			qiIdForExpressions = [];
			ModuleUtils._collectQIIdsInItem(item, qiIds, qiIdForExpressions);
			if (!(qiIds.length==0)) {
				qs = ModuleUtils._getParentQuerySubject(item);
				module = MoserObject.getModuleObject(qs);
				if (module && qs) {
					qsId = qs.getIdentifier();
				}
			}
			if (qsId != null) {
				ModuleUtils._cleanSplitDefinition(module, obj);
			}
		}
		if ("QuerySubject" == parentParentTp) {
			if (bclean) {
				(parentParent).removeItem(item);
			}
		} else if ("QueryItem" == parentParentTp) {
			if (bclean) {
				(parentParent).removeItem(item);
			}
		} else if ("Folder" == parentParentTp) {
			if (bclean) {
				(parentParent).removeItem(item);
			}
		} else {
			return null;
		}
		if (qsId != null) {
			for (var i = 0; i < qiIds.length; ++i) {
				var qiId = qiIds[i];
				PropertySetter.resetReferencedObject(module, qiId, qiIdForExpressions[i], qsId);
				ModuleUtils._cleanItemNormalization(qs, qiId);
			}
		}
		if (bclean) {
			return obj;
		}
		return item;
	}

	static _cleanSplitDefinition(module, obj) {
		if (!obj) {
			return;
		}
		if (!("QueryItem" == obj.getObjectType())) {
			return;
		}
		var sp = (obj).basicGetSplitDefinition();
		if (!sp || sp.length==0 || PropertySetter.isEmptyRef(sp[0].getRef())) {
			return;
		}
		var idForExpr = (obj).getIdForExpression();
		var qss = module.basicGetQuerySubject();
		if (!qss) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = qss, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var aQS = __exp_enFor0[__i_enFor0];
			for (var __i_enFor1 = 0, __exp_enFor1 = PropertySetter.getAllQueryItemsInQuerySubject(aQS), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var aQI = __exp_enFor1[__i_enFor1];
				if (aQI === obj) {
					continue;
				}
				var sp1 = aQI.basicGetSplitDefinition();
				if (!sp1 || sp1.length==0 || !(idForExpr == sp1[0].getRef())) {
					continue;
				}
				sp1[0].setRef("None");
			}
		}
	}

	static _cleanItemNormalization(qs, qiId) {
		var itemNorm = qs.getItemNormalization();
		if (!itemNorm) {
			return;
		}
		var grps = itemNorm.basicGetItemNormalizationGroup();
		if (!grps || grps.length==0) {
			return;
		}
		var grpToDelete;
		for (var __i_enFor0 = 0, __exp_enFor0 = grps, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var g = __exp_enFor0[__i_enFor0];
			var attrs = g.basicGetAttribute();
			var k = g.getKey();
			if (attrs && !(attrs.length==0)) {
				var sz = attrs.length;
				for (var __i_enFor1 = 0, __exp_enFor1 = attrs, __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var a = __exp_enFor1[__i_enFor1];
					if (qiId == a.getItemRef()) {
						if (sz == 1 && !k) {
							grpToDelete = g;
							break;
						}
						g.removeAttribute(a);
						return;
					}
				}
			}
			if (grpToDelete) {
				break;
			}
			if (!k) {
				continue;
			}
			if (qiId == k.getItemRef()) {
				grpToDelete = g;
				break;
			}
		}
		if (grpToDelete) {
			var gpId = grpToDelete.getIdentifier();
			var paranetGrps = grpToDelete.basicGetParentGroupRef();
			itemNorm.removeItemNormalizationGroup(grpToDelete);
			for (var __i_enFor2 = 0, __exp_enFor2 = grps, __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var otherGrp = __exp_enFor2[__i_enFor2];
				var otherParanetGrps = otherGrp.basicGetParentGroupRef();
				if (otherParanetGrps && !(otherParanetGrps.length==0) && otherParanetGrps.indexOf(gpId)> -1) {
					otherGrp.removeParentGroupRef(gpId);
					if (paranetGrps) {
						for (var __i_enFor3 = 0, __exp_enFor3 = paranetGrps, __len_enFor3 = __exp_enFor3.length;
								__i_enFor3 < __len_enFor3; ++__i_enFor3) {
							var sg = __exp_enFor3[__i_enFor3];
							if (!(otherParanetGrps.indexOf(sg)> -1)) {
								otherGrp.addParentGroupRef(sg);
							}
						}
					}
				}
			}
		}
		if (grps.length==0) {
			qs.setItemNormalization(null);
		}
	}

	static _collectQIIdsInItem(itm, qiIds, qiIdForExpressions) {
		var qi = itm.getQueryItem();
		if (qi) {
			var id = qi.getIdentifier();
			var qiIdForExpression = qi.getIdForExpression();
			if (id != null && qiIdForExpression != null) {
				qiIds.push(id);
				qiIdForExpressions.push(qiIdForExpression);
			}
		} else {
			var fld = itm.getFolder();
			if (fld) {
				ModuleUtils._collectQIIdsInFolder(fld, qiIds, qiIdForExpressions);
			}
		}
	}

	static _collectQIIdsInFolder(theFolder, qiIds, qiIdForExpressions) {
		var items = theFolder.basicGetItem();
		if (!items || items.length==0) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			ModuleUtils._collectQIIdsInItem(itm, qiIds, qiIdForExpressions);
		}
	}

	static _checkFolderType(obj) {
		var parent = obj.getParent();
		var parentTp = parent.getObjectType();
		if (!("Module" == parentTp) && !("FolderItemType" == parentTp)) {
			return false;
		}
		if ("Module" == parentTp) {
			return true;
		}
		var folderItem = parent;
		var parentParentTp = ModuleUtils._getParentObjectType(folderItem);
		return "FolderType" == parentParentTp;
	}

	static _collectIdsInFolderType(obj, ids) {
		if (!obj) {
			return;
		}
		var folderItems = obj.basicGetFolderItem();
		if (!folderItems) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = folderItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var folderItem = __exp_enFor0[__i_enFor0];
			var ref = folderItem.getRef();
			if (ref != null) {
				ids.push(ref);
			}
			ModuleUtils._collectIdsInFolderType(folderItem.getFolder(), ids);
		}
	}

	static _removeObjectsInMDTVFolderType(obj) {
		var module = MoserObject.getModuleObject(obj);
		if (!module) {
			return;
		}
		var ids = [];
		ModuleUtils._collectIdsInFolderType(obj, ids);
		if (ids.length==0) {
			return;
		}
		var idToQS = {};
		var idToCalc = {};
		var idToFilter = {};
		var qss = module.basicGetQuerySubject();
		if (qss) {
			for (var __i_enFor0 = 0, __exp_enFor0 = qss, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qs = __exp_enFor0[__i_enFor0];
				idToQS[qs.getIdentifier()] = qs;
			}
		}
		var calcs = module.basicGetCalculation();
		if (calcs) {
			for (var __i_enFor1 = 0, __exp_enFor1 = calcs, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var c = __exp_enFor1[__i_enFor1];
				idToCalc[c.getIdentifier()] = c;
			}
		}
		var filters = module.basicGetFilter();
		if (filters) {
			for (var __i_enFor2 = 0, __exp_enFor2 = filters, __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var f = __exp_enFor2[__i_enFor2];
				idToFilter[f.getIdentifier()] = f;
			}
		}
		var seen = {};
		for (var __i_enFor3 = 0, __exp_enFor3 = ids, __len_enFor3 = __exp_enFor3.length;
				__i_enFor3 < __len_enFor3; ++__i_enFor3) {
			var id = __exp_enFor3[__i_enFor3];
			if (seen.hasOwnProperty(id)) {
				continue;
			}
			seen[id] = 1;
			var q = idToQS[id];
			if (q) {
				ModuleUtils._deleteQuerySubjectRelatedObjects(module, q, id);
				module.removeQuerySubject(q);
				continue;
			}
			var c = idToCalc[id];
			if (c) {
				module.removeCalculation(c);
				continue;
			}
			var f = idToFilter[id];
			if (f) {
				module.removeFilter(f);
				continue;
			}
		}
	}

	static _removeFolderTypeFromMDTV(obj, bClean) {
		if (!(ModuleUtils._checkFolderType(obj))) {
			return null;
		}
		var parent = obj.getParent();
		var parentTp = parent.getObjectType();
		if (!bClean) {
			if ("FolderItemType" == parentTp) {
				return parent;
			}
			return null;
		}
		ModuleUtils._removeObjectsInMDTVFolderType(obj);
		if ("Module" == parentTp) {
			(parent).removeMetadataTreeView(obj);
			return obj;
		}
		var folderItem = parent;
		(folderItem.getParent()).removeFolderItem(folderItem);
		return obj;
	}

	static _removeObjectFromModule(obj, bClean) {
		var module = MoserObject.getModuleObject(obj);
		if (!module) {
			return null;
		}
		var objectTp = obj.getObjectType();
		if ("QuerySubject" == objectTp) {
			return ModuleUtils._deleteQuerySubjectImp(module, obj, bClean);
		}
		var mdtvs = MoserObjectUtils.getList(module.basicGetMetadataTreeView());
		if ("Calculation" == objectTp) {
			var calc = obj;
			for (var __i_enFor0 = 0, __exp_enFor0 = mdtvs, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var folder = __exp_enFor0[__i_enFor0];
				var r = ModuleUtils._removeQuerySubjectFromMDTVFolder(calc.getIdentifier(), folder, bClean);
				if (!bClean && r) {
					return r;
				}
			}
			module.removeCalculation(calc);
			return obj;
		} else if ("Filter" == objectTp) {
			var filter = obj;
			for (var __i_enFor1 = 0, __exp_enFor1 = mdtvs, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var folder = __exp_enFor1[__i_enFor1];
				var r = ModuleUtils._removeQuerySubjectFromMDTVFolder(filter.getIdentifier(), folder, bClean);
				if (!bClean && r) {
					return r;
				}
			}
			module.removeFilter(filter);
			return obj;
		} else {
			return null;
		}
	}

	static move(obj, parentObj, index) {
		var removed = ModuleUtils._removeImp(obj, false);
		if (!removed) {
			return null;
		}
		var p1 = ModuleUtils._getParentContainer(parentObj);
		var p2 = ModuleUtils._getParentContainer(removed);
		if (!p1 || !p2 || !(p1 === p2)) {
			return null;
		}
		var oldParentObj = [null];
		var oldIdx = ModuleUtils._detachFromParent(removed, oldParentObj);
		if (oldIdx == -1) {
			return null;
		}
		ModuleUtils._insertToParent(removed, parentObj, index, oldParentObj[0], oldIdx);
		return obj;
	}

	static _getParentContainer(obj) {
		if (!obj) {
			return null;
		}
		var objType = obj.getObjectType();
		if ("QueryItem" == objType || "QuerySubject" == objType || "Module" == objType) {
			return obj;
		}
		return ModuleUtils._getParentContainer(obj.getParent());
	}

	static _detachFromParent(removed, oldParent) {
		var parent = removed.getParent();
		var parentObjType = parent.getObjectType();
		var objType = removed.getObjectType();
		var oldIdx;
		if ("QuerySubject" == parentObjType) {
			var qs = parent;
			if ("ItemType" == objType) {
				oldIdx = qs.getItem().indexOf(removed);
				if (oldIdx >= 0) {
					qs.removeItemAt(oldIdx);
					oldParent[0] = qs;
				}
			} else if ("Filter" == objType) {
				oldIdx = qs.getFilter().indexOf(removed);
				if (oldIdx >= 0) {
					qs.removeFilterAt(oldIdx);
					oldParent[0] = qs;
				}
			} else if ("SecurityFilter" == objType) {
				oldIdx = qs.getSecurityFilter().indexOf(removed);
				if (oldIdx >= 0) {
					qs.removeSecurityFilterAt(oldIdx);
					oldParent[0] = qs;
				}
			} else {
				return -1;
			}
		} else if ("Folder" == parentObjType) {
			if ("ItemType" == objType) {
				oldIdx = (parent).getItem().indexOf(removed);
				if (oldIdx >= 0) {
					(parent).removeItemAt(oldIdx);
					oldParent[0] = parent;
				}
			} else {
				return -1;
			}
		} else if ("QueryItem" == parentObjType) {
			if ("ItemType" == objType) {
				oldIdx = (parent).getItem().indexOf(removed);
				if (oldIdx >= 0) {
					(parent).removeItemAt(oldIdx);
					oldParent[0] = parent;
				}
			} else {
				return -1;
			}
		} else if ("FolderType" == parentObjType) {
			if ("FolderItemType" == objType) {
				oldIdx = (parent).getFolderItem().indexOf(removed);
				if (oldIdx >= 0) {
					(parent).removeFolderItemAt(oldIdx);
					oldParent[0] = parent;
				}
			} else {
				return -1;
			}
		} else {
			return -1;
		}
		removed.setParent(null);
		return oldIdx;
	}

	static _adjustIndex(i, parent, oldIdx, oldParent) {
		var index = i;
		if (oldParent && parent && parent === oldParent && index > oldIdx) {
			index--;
		}
		return index;
	}

	static _insertToParent(removed, parentObj, index, oldParent, oldIdx) {
		var objType = removed.getObjectType();
		if ("FolderItemType" == objType) {
			var parentFolderType = ModuleUtils._getParentFolderType(parentObj);
			if (!parentFolderType) {
				return;
			}
			var idx = ModuleUtils._adjustIndex(index, parentFolderType, oldIdx, oldParent);
			if (ModuleUtils._invalidIndex(parentFolderType.basicGetFolderItem(), idx)) {
				parentFolderType.addFolderItem(removed);
			} else {
				parentFolderType.addFolderItemAt(idx, removed);
			}
		} else if ("ItemType" == objType) {
			var idx = ModuleUtils._adjustIndex(index, parentObj, oldIdx, oldParent);
			ModuleUtils._addItemToParent(removed, parentObj, idx);
		} else if ("Filter" == objType) {
			var idx = ModuleUtils._adjustIndex(index, parentObj, oldIdx, oldParent);
			if (ModuleUtils._invalidIndex((parentObj).basicGetFilter(), idx)) {
				(parentObj).addFilter(removed);
			} else {
				(parentObj).addFilterAt(idx, removed);
			}
		} else if ("SecurityFilter" == objType) {
			var idx = ModuleUtils._adjustIndex(index, parentObj, oldIdx, oldParent);
			if (ModuleUtils._invalidIndex((parentObj).basicGetSecurityFilter(), idx)) {
				(parentObj).addSecurityFilter(removed);
			} else {
				(parentObj).addSecurityFilterAt(idx, removed);
			}
		}
	}

	static getExtractIdentifiersSpec(obj, value) {
		return PropertySetter.getExtractIdentifiersSpec(obj, value);
	}

	static setIdentifier(obj, value, response) {
		var idforExprToNewExpr;
		if (response) {
			var feedBack = response["feedback"];
			if (feedBack && !(feedBack.length==0)) {
				idforExprToNewExpr = {};
				for (var __i_enFor0 = 0, __exp_enFor0 = feedBack, __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var f = __exp_enFor0[__i_enFor0];
					var jobj = f;
					idforExprToNewExpr[jobj["idForExpression"]] = jobj["expression"];
				}
			}
		}
		PropertySetter.setIdentifier(obj, value, idforExprToNewExpr);
	}

	/**
	 * Returns a boolean that says, is the MoserObject visible or hidden. Any object with an ancestor (folder/query subject) marked as hidden, should be returned as hidden
	 * @param {ObjectType} object - object in a module
	 * @param {Module} module - module owning the object
	 * @return {boolean}
	 */
	static isVisible(object, module) {
		var hidden = object.isHidden() == null ? false : object.isHidden(), visible = !hidden;
		if (hidden) {
			return visible;
		}
		var parent = ModuleUtils.getTreeAncestor(object, null, module);
		if (parent) {
			return visible && ModuleUtils.isVisible(parent, module);
		}
		return visible;
	}

	/**
	 * find the ancestor in the tree.
	 * @param {MoserObject} obj
	 * @param {String[]} anestorType
	 * @param {Module} module  TODO
	 * @return {MoserObject}
	 */
	static getTreeAncestor(obj, anestorType, module) {
		var objTp = obj.getObjectType();
		if (!(ModuleUtils._isMDTVObjectType(objTp, false))) {
			return null;
		}
		if (!module) {
			module = MoserObject.getModuleObject(obj);
		}
		var ancestorSet;
		if (anestorType && anestorType.length > 0) {
			ancestorSet = {};
			for (var __i_enFor0 = 0, __len_enFor0 = anestorType.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var element = anestorType[__i_enFor0];
				ancestorSet[element] = 1;
			}
		}
		if ("QuerySubject" == objTp || "Calculation" == objTp) {
			var folderItemType = ModuleUtils._getFolderItemType(module, (obj).getIdentifier());
			if (!folderItemType) {
				if ("Calculation" == objTp) {
					return obj.getParent() && obj.getParent().getParent() ? obj.getParent().getParent() : null;
				}
				return null;
			}
			return ModuleUtils._getTreeAncestorMDTV(module, folderItemType.getParent(), ancestorSet);
		}
		if ("FolderType" == objTp) {
			return ModuleUtils._getTreeAncestorMDTV(module, obj.getParent(), ancestorSet);
		}
		if ("Filter" == objTp || "NamedSet" == objTp) {
			var qs = ModuleUtils._getParentQuerySubject(obj);
			if (!qs) {
				var folderItemType = ModuleUtils._getFolderItemType(module, (obj).getIdentifier());
				if (!folderItemType) {
					return null;
				}
				return ModuleUtils._getTreeAncestorMDTV(module, folderItemType.getParent(), ancestorSet);
			}
			return ModuleUtils._getTreeAncestorQuerySubject(module, obj.getParent(), ancestorSet);
		}
		return ModuleUtils._getTreeAncestorQuerySubject(module, obj.getParent(), ancestorSet);
	}

	static _isMDTVObjectType(objectType, incModule) {
		if ("QueryItem" == objectType || "Folder" == objectType || "Filter" == objectType || "QuerySubject" == objectType || "Calculation" == objectType || "FolderType" == objectType || "NamedSet" == objectType || "MemberType" == objectType) {
			return true;
		}
		return incModule && "Module" == objectType;
	}

	static _getTreeAncestorQuerySubject(module, parentObj, ancestorSet) {
		if (!parentObj) {
			return null;
		}
		var objType = parentObj.getObjectType();
		if ("QuerySubject" == objType) {
			if (!ancestorSet || ancestorSet.hasOwnProperty(objType)) {
				return parentObj;
			}
			var folderItemType = ModuleUtils._getFolderItemType(module, (parentObj).getIdentifier());
			if (!folderItemType) {
				return null;
			}
			return ModuleUtils._getTreeAncestorMDTV(module, folderItemType.getParent(), ancestorSet);
		}
		if (ModuleUtils._isMDTVObjectType(objType, false) && (!ancestorSet || ancestorSet.hasOwnProperty(objType))) {
			return parentObj;
		}
		return ModuleUtils._getTreeAncestorQuerySubject(module, parentObj.getParent(), ancestorSet);
	}

	static _getTreeAncestorMDTV(module, parent, ancestorSet) {
		if (!parent) {
			return null;
		}
		var objType = parent.getObjectType();
		if (ModuleUtils._isMDTVObjectType(objType, true) && (!ancestorSet || ancestorSet.hasOwnProperty(objType))) {
			if (parent.getParent() == module) {
				return module;
			}
			return parent;
		}
		return ModuleUtils._getTreeAncestorMDTV(module, parent.getParent(), ancestorSet);
	}

	static _getFolderItemType(module, id) {
		if (!module) {
			return null;
		}
		var mdtvs = MoserObjectUtils.getList(module.basicGetMetadataTreeView());
		for (var __i_enFor0 = 0, __exp_enFor0 = mdtvs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var folder = __exp_enFor0[__i_enFor0];
			var r = ModuleUtils._getFolderItemTypeInMDTVFolder(id, folder);
			if (r) {
				return r;
			}
		}
		return null;
	}

	static _getFolderItemTypeInMDTVFolder(id, folder) {
		if (!folder) {
			return null;
		}
		var itemList = MoserObjectUtils.getList(folder.basicGetFolderItem());
		for (var j = itemList.length - 1; j >= 0; --j) {
			var r = itemList[j];
			if (id == r.getRef() || r.getDefLink() && id == r.getDefLink().getRef()) {
				return r;
			}
			r = ModuleUtils._getFolderItemTypeInMDTVFolder(id, r.getFolder());
			if (r) {
				return r;
			}
		}
		return null;
	}

	static findTreeObjectByIdForExpression(idForExpression, treeObject, forceMDTVSearch) {
		if (idForExpression == null || idForExpression.length === 0 || !treeObject) {
			return null;
		}
		if (!forceMDTVSearch) {
			return ModuleUtils._findObjectByIdforExprInObject(treeObject, idForExpression);
		}
		var module = MoserObject.getModuleObject(treeObject);
		if (!module) {
			return null;
		}
		var mdtvs = MoserObjectUtils.getList(module.basicGetMetadataTreeView());
		for (var __i_enFor0 = 0, __exp_enFor0 = mdtvs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var folder = __exp_enFor0[__i_enFor0];
			var r = ModuleUtils._findObjectByIdforExprInObject(folder, idForExpression);
			if (r) {
				return r;
			}
		}
		return null;
	}

	static _findObjectByIdforExprInObject(obj, idForExpression) {
		if (!obj) {
			return null;
		}
		var objType = obj.getObjectType();
		if ("FolderType" == objType) {
			return ModuleUtils._findObjectByIdforExprInFolderType(obj, idForExpression);
		}
		if ("FolderItemType" == objType) {
			return ModuleUtils._findObjectByIdforExprInFolderItemType(obj, idForExpression);
		}
		if ("QuerySubject" == objType) {
			return ModuleUtils._findObjectByIdforExprInQuerySubject(obj, idForExpression);
		}
		if ("ItemType" == objType) {
			return ModuleUtils._findObjectByIdforExprInItemType(obj, idForExpression);
		}
		if ("QueryItem" == objType) {
			return ModuleUtils._findObjectByIdforExprInQueryItem(obj, idForExpression);
		}
		if ("Folder" == objType) {
			return ModuleUtils._findObjectByIdforExprInFolder(obj, idForExpression);
		}
		if ("Calculation" == objType || "Filter" == objType || "NamedSet" == objType) {
			var idforexpr = (obj).getIdForExpression();
			if (idForExpression == idforexpr) {
				return obj;
			}
			return null;
		}
		return null;
	}

	static _findObjectByIdforExprInFolderType(obj, idForExpression) {
		var items = MoserObjectUtils.getList(obj.basicGetFolderItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			var r = ModuleUtils._findObjectByIdforExprInObject(itm, idForExpression);
			if (r) {
				return r;
			}
		}
		return null;
	}

	static _findObjectByIdforExprInFolderItemType(obj, idForExpression) {
		var folder = obj.getFolder();
		if (folder) {
			return ModuleUtils._findObjectByIdforExprInObject(folder, idForExpression);
		}
		var ref = obj.getRef();
		if (ref != null && !(ref.length === 0)) {
			return ModuleUtils._findObjectByIdforExprInObject(obj.getReferencedObject(), idForExpression);
		}
		var dlink = obj.getDefLink();
		if (dlink) {
			var rf = dlink.getRef();
			if (rf != null && !(rf.length === 0)) {
				return ModuleUtils._findObjectByIdforExprInObject(dlink.getReferencedObject(), idForExpression);
			}
		}
		return null;
	}

	static _findObjectByIdforExprInQuerySubject(obj, idForExpression) {
		var idforexpr = obj.getIdForExpression();
		if (idForExpression == idforexpr) {
			return obj;
		}
		var items = MoserObjectUtils.getList(obj.basicGetItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			var r = ModuleUtils._findObjectByIdforExprInObject(itm, idForExpression);
			if (r) {
				return r;
			}
		}
		return null;
	}

	static _findObjectByIdforExprInItemType(obj, idForExpression) {
		var queryItem = obj.getQueryItem();
		if (queryItem) {
			return ModuleUtils._findObjectByIdforExprInObject(queryItem, idForExpression);
		}
		var nameSet = obj.getNamedSet();
		if (nameSet) {
			return ModuleUtils._findObjectByIdforExprInObject(nameSet, idForExpression);
		}
		var filter = obj.getFilter();
		if (filter) {
			return ModuleUtils._findObjectByIdforExprInObject(filter, idForExpression);
		}
		var folder = obj.getFolder();
		if (folder) {
			return ModuleUtils._findObjectByIdforExprInObject(folder, idForExpression);
		}
		return null;
	}

	static _findObjectByIdforExprInFolder(obj, idForExpression) {
		var items = MoserObjectUtils.getList(obj.basicGetItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			var r = ModuleUtils._findObjectByIdforExprInObject(itm, idForExpression);
			if (r) {
				return r;
			}
		}
		return null;
	}

	static _findObjectByIdforExprInQueryItem(obj, idForExpression) {
		var idforexpr = obj.getIdForExpression();
		if (idForExpression == idforexpr) {
			return obj;
		}
		var items = MoserObjectUtils.getList(obj.basicGetItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			var r = ModuleUtils._findObjectByIdforExprInObject(itm, idForExpression);
			if (r) {
				return r;
			}
		}
		return null;
	}

	static getTreeChildren(obj, depth, childrenType) {
		if (!obj || depth < 0) {
			return null;
		}
		var objTp = obj.getObjectType();
		if (!(ModuleUtils._isMDTVObjectType(objTp, true))) {
			return null;
		}
		var childrenSet;
		if (childrenType && childrenType.length > 0) {
			childrenSet = {};
			for (var __i_enFor0 = 0, __len_enFor0 = childrenType.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var element = childrenType[__i_enFor0];
				childrenSet[element] = 1;
			}
		}
		var rt = [];
		if ("Module" == objTp) {
			var mdtv = (obj).basicGetMetadataTreeView();
			if (!mdtv) {
				return rt;
			}
			for (var __i_enFor1 = 0, __exp_enFor1 = mdtv, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var folder = __exp_enFor1[__i_enFor1];
				ModuleUtils._getTreeChildrenObjects(folder, depth, 1, childrenSet, rt);
			}
			return rt;
		}
		ModuleUtils._getTreeChildrenObjects(obj, depth, 1, childrenSet, rt);
		return rt;
	}

	static _getTreeChildrenObjects(parent, depth, d, childrenSet, rt) {
		if (depth != 0 && d > depth) {
			return;
		}
		var children = ModuleUtils._getVisibleChildren(parent);
		for (var __i_enFor0 = 0, __exp_enFor0 = children, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var c = __exp_enFor0[__i_enFor0];
			var cType = c.getObjectType();
			if (!childrenSet || childrenSet.hasOwnProperty(cType)) {
				rt.push(c);
			}
			ModuleUtils._getTreeChildrenObjects(c, depth, d + 1, childrenSet, rt);
		}
	}

	static _getVisibleChildren(parent) {
		var children = [];
		var parentType = parent.getObjectType();
		if ("FolderType" == parentType) {
			var itms = (parent).basicGetFolderItem();
			if (itms) {
				for (var __i_enFor0 = 0, __exp_enFor0 = itms, __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var itm = __exp_enFor0[__i_enFor0];
					var fldType = itm.getFolder();
					if (fldType) {
						children.push(fldType);
						continue;
					}
					var o = itm.getReferencedObject();
					if (o) {
						children.push(o);
						continue;
					}
					var dlink = itm.getDefLink();
					if (dlink) {
						var refobj = dlink.getReferencedObject();
						if (refobj) {
							children.push(refobj);
						}
					}
				}
			}
		} else if ("QueryItem" == parentType || "Folder" == parentType || "QuerySubject" == parentType) {
			var items = null;
			var members = null;
			if ("QueryItem" == parentType) {
				items = (parent).basicGetItem();
				members = (parent).basicGetMembers();
			} else if ("Folder" == parentType) {
				items = (parent).basicGetItem();
			} else {
				items = (parent).basicGetItem();
			}
			if (items) {
				for (var __i_enFor1 = 0, __exp_enFor1 = items, __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var item = __exp_enFor1[__i_enFor1];
					var qi = item.getQueryItem();
					if (qi) {
						children.push(qi);
						continue;
					}
					var fld = item.getFolder();
					if (fld) {
						children.push(fld);
						continue;
					}
					var flt = item.getFilter();
					if (flt) {
						children.push(flt);
						continue;
					}
					var nmSet = item.getNamedSet();
					if (nmSet) {
						children.push(nmSet);
					}
				}
			}
			if (members) {
				Array.prototype.push.apply(children, members);
			}
		} else if ("MemberType" == parentType) {
			var members = (parent).basicGetMembers();
			if (members) {
				Array.prototype.push.apply(children, members);
			}
		}
		return children;
	}

	static copyModule(srcModule) {
		var clonedModule = srcModule.clone();
		ModuleUtils._resetRefObject(clonedModule);
		ReferenceResolver.resolve(clonedModule);
		return clonedModule;
	}

	static _resetRefObject(module) {
		if (!module) {
			return;
		}
		var overrideMode = module.isOverrideMode();
		module.setOverrideMode(false);
		ModuleUtils._resetItemHierarchyLocalRef(module);
		var mdtv = module.basicGetMetadataTreeView();
		if (mdtv) {
			for (var __i_enFor0 = 0, __exp_enFor0 = mdtv, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var folderType = __exp_enFor0[__i_enFor0];
				ModuleUtils._resetFolderTypeReferences(folderType);
			}
		}
		ModuleUtils._resetSegmentReferences(module);
		ModuleUtils._resetRelationships(module);
		ModuleUtils._resetFacetDef(module);
		module.setOverrideMode(overrideMode);
	}

	static _resetItemHierarchyLocalRef(module) {
		var querySubjectList = module.basicGetQuerySubject();
		if (!querySubjectList) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = querySubjectList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var querySubject = __exp_enFor0[__i_enFor0];
			var queryItemTypeList = querySubject.basicGetItem();
			if (!queryItemTypeList) {
				continue;
			}
			for (var __i_enFor1 = 0, __exp_enFor1 = queryItemTypeList, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var queryItemType = __exp_enFor1[__i_enFor1];
				var hierarchy = queryItemType.getItemHierarchy();
				if (!hierarchy) {
					continue;
				}
				var localRefList = hierarchy.basicGetLocalRef();
				if (!localRefList) {
					continue;
				}
				for (var __i_enFor2 = 0, __exp_enFor2 = localRefList, __len_enFor2 = __exp_enFor2.length;
						__i_enFor2 < __len_enFor2; ++__i_enFor2) {
					var localRef = __exp_enFor2[__i_enFor2];
					var ref = localRef.getRef();
					if (ref != null) {
						localRef.setReferencedObject(null);
					}
				}
			}
		}
	}

	static _resetFolderTypeReferences(folderType) {
		var folderItemTypes = folderType.basicGetFolderItem();
		if (!folderItemTypes) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = folderItemTypes, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var folderItemType = __exp_enFor0[__i_enFor0];
			var ref = folderItemType.getRef();
			if (ref != null) {
				folderItemType.setReferencedObject(null);
			} else {
				var defLinkType = folderItemType.getDefLink();
				if (defLinkType) {
					defLinkType.setReferencedObject(null);
				}
			}
			var nestedFolder = folderItemType.getFolder();
			if (nestedFolder) {
				ModuleUtils._resetFolderTypeReferences(nestedFolder);
			}
		}
	}

	static _resetSegmentReferences(module) {
		var drillGroups = module.basicGetDrillGroup();
		if (!drillGroups) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = drillGroups, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var drillGroup = __exp_enFor0[__i_enFor0];
			var segments = drillGroup.basicGetSegment();
			if (!segments) {
				continue;
			}
			for (var __i_enFor1 = 0, __exp_enFor1 = segments, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var segment = __exp_enFor1[__i_enFor1];
				segment.setReferencedObject(null);
			}
		}
	}

	static _resetRelationships(module) {
		var relationships = module.basicGetRelationship();
		if (!relationships) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = relationships, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var relationship = __exp_enFor0[__i_enFor0];
			var leftcard = relationship.getLeft();
			if (leftcard) {
				leftcard.setReferencedObject(null);
			}
			var rightcard = relationship.getRight();
			if (rightcard) {
				rightcard.setReferencedObject(null);
			}
			var links = relationship.basicGetLink();
			if (!links) {
				continue;
			}
			for (var __i_enFor1 = 0, __exp_enFor1 = links, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var link = __exp_enFor1[__i_enFor1];
				link.setReferencedObjectLeft(null);
				link.setReferencedObjectRight(null);
			}
		}
	}

	static _resetFacetDef(module) {
		var querySubjects = module.basicGetQuerySubject();
		if (!querySubjects) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = querySubjects, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var querySubject = __exp_enFor0[__i_enFor0];
			var itemTypeList = querySubject.basicGetItem();
			ModuleUtils._resetFacetDefFromItems(itemTypeList);
		}
	}

	static _resetFacetDefFromItems(itemTypeList) {
		if (!itemTypeList) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = itemTypeList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itemType = __exp_enFor0[__i_enFor0];
			var nested = null;
			var queryItem = itemType.getQueryItem();
			if (queryItem) {
				ModuleUtils._resetFacetDefFromQueryItem(queryItem);
				nested = queryItem.basicGetItem();
			} else {
				var nestedFolder = itemType.getFolder();
				if (nestedFolder) {
					nested = nestedFolder.basicGetItem();
				}
			}
			ModuleUtils._resetFacetDefFromItems(nested);
		}
	}

	static _resetFacetDefFromQueryItem(item) {
		var ft = item.getFacetDefinition();
		if (!ft) {
			return;
		}
		var sl = ft.getSortList();
		if (sl) {
			var sortItems = sl.basicGetSortItem();
			if (sortItems) {
				for (var __i_enFor0 = 0, __exp_enFor0 = sortItems, __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var sortItem = __exp_enFor0[__i_enFor0];
					sortItem.setReferencedObject(null);
				}
			}
		}
		var context = ft.getContext();
		if (context) {
			context.setReferencedObject(null);
		}
	}

	static isForData(mo) {
		return mo instanceof MemberType;
	}

	/**
	 * Sets the member to hidden. Members retrieved from expanding the members folder or from doing a member search, are both added to the same tree. When members are retrieved from search results, they are marked as hidden. They're put in the tree for the sole purpose of having them have the proper parentage so that the UI code behaves properly. Only when members are added when expanding the members folder (i.e when createMembers() is called) are they marked as not hidden. NOTE: There is a setHidden on parent class ObjectType which could've been used, but its purpose to determine if the metadata item is visible. It was decided not to overload this.
	 * @param {MemberType} member  - Input member.
	 * @param {boolean} hidden  - Hidden flag.
	 */
	static setMemberHidden(member, hidden) {
		var prop = ModuleUtils.getMemberHiddenProperty(member);
		if (!prop) {
			prop = new PropertyType();
			prop.setName("memberHidden");
			member.addProperty(prop);
		}
		if (hidden) {
			prop.setValue("true");
		} else {
			prop.setValue("false");
		}
	}

	/**
	 * @param {MemberType} member
	 * @return {PropertyType}
	 */
	static getMemberHiddenProperty(member) {
		for (var __i_enFor0 = 0, __exp_enFor0 = member.getProperty(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var p = __exp_enFor0[__i_enFor0];
			if ("memberHidden" == p.getName()) {
				return p;
			}
		}
		return null;
	}

	static getMemberHidden(member) {
		var p = ModuleUtils.getMemberHiddenProperty(member);
		if (p) {
			return "true" == p.getValue();
		}
		return false;
	}

	/**
	 * clean  an object. this function does not allow undo.
	 * @param {MoserObject} obj  object to clean.
	 * @param {Array} objTypes  objects to be cleaned. If empty, to default clean.
	 * @return {boolean}  t/f.
	 */
	static cleanObject(obj, objTypes) {
		var objectType = obj.getObjectType();
		if ("QuerySubject" == objectType) {
			return ModuleUtils._cleanQuerySubject(obj, objTypes);
		}
		return false;
	}

	static _cleanQuerySubject(qs, objTypes) {
		return ModuleUtils._cleanQuerySubjectItemNormalization(qs, objTypes);
	}

	static _cleanQuerySubjectItemNormalization(qs, objTypes) {
		var itemNorm = qs.getItemNormalization();
		if (!itemNorm) {
			return false;
		}
		var grps = itemNorm.basicGetItemNormalizationGroup();
		if (!grps || grps.length==0) {
			return false;
		}
		if (objTypes && !(objTypes.length==0) && !(objTypes.indexOf("ItemNormalizationType")> -1)) {
			return false;
		}
		var existingQIs = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = MoserObjectUtils.getQueryItems(qs), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			existingQIs.push(qi.getIdentifier());
		}
		return PropertySetter.cleanQuerySubjectItemNormalization(qs, existingQIs);
	}

	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/ModuleUtils.js